import { useState, useEffect, useRef } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
  updatePassword,
  deleteUser,
  EmailAuthProvider,
  reauthenticateWithCredential,
  linkWithCredential,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyDc3CRvIWhbRwy_Ibs309OY92jajyZ-TeI",
  authDomain: "scholarx-6440c.firebaseapp.com",
  projectId: "scholarx-6440c",
  storageBucket: "scholarx-6440c.firebasestorage.app",
  messagingSenderId: "532310433134",
  appId: "1:532310433134:web:6dcb6094109b8d6592b57f",
};

const firebaseApp = initializeApp(firebaseConfig);
const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);
const googleProvider = new GoogleAuthProvider();

const PLANS = {
  free: {
    name: "Free",
    price: "$0",
    period: "forever",
    questionsPerDay: 8,
    subjects: ["Math", "Science", "History", "English", "Languages", "Coding", "Test Prep", "College Essays"],
    features: ["8 questions/day", "All subjects", "Photo scanning", "AI Humanizer", "AI Detector"],
    color: "#94a3b8",
    badge: "🎒",
  },
  scholar: {
    name: "Scholar",
    price: "$7.99",
    period: "/month",
    questionsPerDay: 50,
    subjects: ["Math", "Science", "History", "English", "Languages", "Coding", "Test Prep", "College Essays"],
    features: ["50 questions/day", "All subjects", "Photo scanning", "AI Humanizer", "AI Detector", "Step-by-step breakdowns"],
    color: "#3b82f6",
    badge: "⭐",
  },
  genius: {
    name: "Genius",
    price: "$13.99",
    period: "/month",
    questionsPerDay: Infinity,
    subjects: ["Math", "Science", "History", "English", "Languages", "Coding", "Test Prep", "College Essays"],
    features: ["Unlimited questions", "All subjects", "Photo scanning", "AI Humanizer", "AI Detector", "College essay help", "Priority AI"],
    color: "#f59e0b",
    badge: "🏆",
  },
};

const SUBJECTS = ["Math", "Science", "History", "English", "Languages", "Coding", "Test Prep", "College Essays"];
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
// Key secured server-side via Firebase Functions

function getTodayKey() {
  return new Date().toISOString().slice(0, 10);
}

function loadState() {
  try {
    return JSON.parse(localStorage.getItem("scholarsensai") || "{}");
  } catch {
    return {};
  }
}

function saveState(data) {
  try {
    localStorage.setItem("scholarsensai", JSON.stringify(data));
  } catch {}
}

function loadUsage() {
  try {
    const today = getTodayKey();
    const saved = JSON.parse(localStorage.getItem("scholarsensai_usage") || "{}");
    if (saved.date === today) return saved.count || 0;
    return 0;
  } catch { return 0; }
}

function saveUsage(count) {
  try {
    const today = getTodayKey();
    localStorage.setItem("scholarsensai_usage", JSON.stringify({date: today, count}));
  } catch {}
}

function loadSubjectHistory(subject) {
  try {
    const key = "scholarsensai_history_" + subject.replace(/\s+/g, "_");
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const {messages, savedAt} = JSON.parse(raw);
    if (Date.now() - savedAt > ONE_WEEK_MS) {
      localStorage.removeItem(key);
      return [];
    }
    return messages || [];
  } catch {
    return [];
  }
}

function saveSubjectHistory(subject, messages, userId) {
  try {
    const key = "scholarsensai_history_" + subject.replace(/\s+/g, "_");
    localStorage.setItem(key, JSON.stringify({messages, savedAt: Date.now()}));
  } catch {}
  if (userId) {
    try {
      const firestoreKey = subject.replace(/\s+/g, "_");
      setDoc(doc(db, "users", userId, "history", firestoreKey), {
        messages: messages.map(m => ({
          role: m.role,
          content: m.content || "",
          subject: m.subject || subject,
          image: m.image || null,
          graph: m.graph || null,
        })),
        savedAt: Date.now(),
      }).catch(() => {});
    } catch {}
  }
}

async function loadSubjectHistoryFromFirestore(subject, userId) {
  try {
    const firestoreKey = subject.replace(/\s+/g, "_");
    const snap = await getDoc(doc(db, "users", userId, "history", firestoreKey));
    if (snap.exists()) {
      const data = snap.data();
      if (Date.now() - data.savedAt < 7 * 24 * 60 * 60 * 1000) {
        return data.messages || [];
      }
    }
    return null;
  } catch {
    return null;
  }
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function callAI(systemPrompt, userPrompt) {
  const response = await fetch("https://us-central1-scholarx-6440c.cloudfunctions.net/aiProxy", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "HTTP-Referer": "https://scholarx-6440c.web.app",
    },
    body: JSON.stringify({
      model: "meta-llama/llama-3.3-70b-instruct",
      max_tokens: 1500,
      messages: [
        {role: "system", content: systemPrompt},
        {role: "user", content: userPrompt},
      ],
    }),
  });
  const data = await response.json();
  return data.choices?.[0]?.message?.content || "";
}

function formatMath(text) {
  return text.replace(/\^(\d+)/g, (match, exp) => `<sup>${exp}</sup>`);
}

function isQuizRequest(text) {
  if (!text) return false;
  const t = text.toLowerCase().trim();
  // Must be a SHORT message (under 200 chars) — pasted code is never a quiz request
  if (t.length > 200) return false;
  return (
    t.includes("quiz me") ||
    t.includes("make me a quiz") ||
    t.includes("give me a quiz") ||
    (t.includes("quiz on") && t.length < 80) ||
    t.includes("create a quiz") ||
    t.includes("make a quiz") ||
    (t === "test me" || t.startsWith("test me on") || t.startsWith("test me with")) ||
    t.includes("practice quiz") ||
    t.includes("generate a quiz") ||
    t.includes("make me practice questions") ||
    (t.includes("practice questions") && t.length < 80)
  );
}

function isGraphRequest(text) {
  if (!text) return false;
  const t = text.toLowerCase().trim();
  return (
    t.includes("graph ") ||
    t.includes("plot ") ||
    t.includes("draw the graph") ||
    t.includes("graph of") ||
    t.includes("plot of") ||
    t.includes("show the graph") ||
    t.includes("generate a graph") ||
    t.includes("can you graph") ||
    t.includes("graph this") ||
    t.includes("plot this") ||
    (t.includes("y=") && (t.includes("graph") || t.includes("plot") || t.includes("show"))) ||
    t.match(/graph\s+y\s*=/) ||
    t.match(/plot\s+y\s*=/) ||
    t.match(/graph\s+f\(x\)/)
  );
}

// ─────────────────────────────────────────────────────────────
// ROBUST MATH EXPRESSION CLEANER — handles all standard forms
// ─────────────────────────────────────────────────────────────
function cleanMathExpr(raw) {
  let e = raw.trim();
  // Strip trailing punctuation
  e = e.replace(/[.,;!?]+$/, "").trim();
  // Strip y= or f(x)= prefix
  e = e.replace(/^\s*y\s*=\s*/i, "");
  e = e.replace(/^\s*f\s*\(\s*x\s*\)\s*=\s*/i, "");
  // Remove all whitespace for consistent parsing
  e = e.replace(/\s+/g, "");

  // Absolute value bars: |x| -> Math.abs(x)
  e = e.replace(/\|([^|]+)\|/g, "Math.abs($1)");

  // Exponents: ^ -> **
  e = e.replace(/\^/g, "**");

  // ── Function name replacements FIRST (before implicit mult) ──
  // Most-specific multi-char names before shorter ones to avoid partial matches.
  e = e.replace(/(?<!Math\.)asin/g, "Math.asin");
  e = e.replace(/(?<!Math\.)acos/g, "Math.acos");
  e = e.replace(/(?<!Math\.)atan/g, "Math.atan");
  e = e.replace(/(?<!Math\.)sqrt/g, "Math.sqrt");
  e = e.replace(/(?<!Math\.)cbrt/g, "Math.cbrt");
  e = e.replace(/(?<!Math\.)floor/g, "Math.floor");
  e = e.replace(/(?<!Math\.)ceil/g, "Math.ceil");
  e = e.replace(/(?<!Math\.)round/g, "Math.round");
  e = e.replace(/(?<!Math\.)exp/g, "Math.exp");
  e = e.replace(/(?<!Math\.)abs/g, "Math.abs");
  // sin/cos/tan after a-prefixed variants; lookbehind prevents re-matching asin's "sin"
  e = e.replace(/(?<![Aa])(?<!Math\.)sin/g, "Math.sin");
  e = e.replace(/(?<![Aa])(?<!Math\.)cos/g, "Math.cos");
  e = e.replace(/(?<![Aa])(?<!Math\.)tan/g, "Math.tan");
  // Logs: ln first, then log10/log2, then bare log
  e = e.replace(/(?<!Math\.)ln/g, "Math.log");
  e = e.replace(/(?<!Math\.)log10/g, "Math.log10");
  e = e.replace(/(?<!Math\.)log2/g, "Math.log2");
  e = e.replace(/(?<!Math\.)log(?!1|2)/g, "Math.log10");

  // ── Constants ──
  e = e.replace(/(?<![.A-Za-z])pi(?![A-Za-z])/g, "Math.PI");
  e = e.replace(/(?<![.A-Za-z\d])e(?![A-Za-z\d])/g, "Math.E");

  // ── Implicit multiplication AFTER fn replacements ──
  // 2x -> 2*x
  e = e.replace(/(\d)(x)/g, "$1*x");
  // digit-( only when digit is at start of a number literal (not ending a fn name like log10)
  e = e.replace(/(?<![A-Za-z_0-9])(\d)\(/g, "$1*(");
  // )( -> )*(
  e = e.replace(/\)\(/g, ")*(");
  // )x -> )*x
  e = e.replace(/\)(x)/g, ")*$1");
  // 2Math.sin -> 2*Math.sin  (coefficient before an already-replaced function)
  e = e.replace(/(\d)(Math\.)/g, "$1*$2");

  return e;
}

// Safe evaluator — returns NaN on any error
function safeEval(expr, x) {
  try {
    const cleaned = cleanMathExpr(expr);
    // eslint-disable-next-line no-new-func
    return Function("x", `"use strict"; return (${cleaned})`)(x);
  } catch {
    return NaN;
  }
}

// Auto-zoom: sample the function over a wide range and return sensible bounds
function computeGraphBounds(expr, xMin = -10, xMax = 10) {
  const samples = 400;
  const yVals = [];
  for (let i = 0; i <= samples; i++) {
    const x = xMin + (i / samples) * (xMax - xMin);
    const y = safeEval(expr, x);
    if (isFinite(y)) yVals.push(y);
  }
  if (yVals.length === 0) return { xMin, xMax, yMin: -8, yMax: 8 };
  const lo = Math.min(...yVals);
  const hi = Math.max(...yVals);
  const span = hi - lo;
  const pad = Math.max(span * 0.15, 1);
  let yMin = lo - pad;
  let yMax = hi + pad;
  // Clamp runaway ranges (e.g. asymptotes)
  if (yMax - yMin > 200) { yMin = -20; yMax = 20; }
  if (yMax - yMin < 2) { yMin -= 2; yMax += 2; }
  return { xMin, xMax, yMin, yMax };
}

function extractGraphExpr(text) {
  let t = text
    .replace(/generate\s+a\s+graph\s+for\s*/i, "")
    .replace(/can\s+you\s+graph\s*/i, "")
    .replace(/please\s+graph\s*/i, "")
    .replace(/draw\s+the\s+graph\s+of\s*/i, "")
    .replace(/draw\s+a\s+graph\s+of\s*/i, "")
    .replace(/graph\s+of\s*/i, "")
    .replace(/plot\s+of\s*/i, "")
    .replace(/show\s+me\s+the\s+graph\s+of\s*/i, "")
    .replace(/show\s+me\s*/i, "")
    .replace(/graph\s+for\s*/i, "")
    .replace(/plot\s+for\s*/i, "")
    .replace(/graph\s+solve[:\s]*/i, "")
    .replace(/graph\s+it[:\s]*/i, "")
    .replace(/graph\s+this[:\s]*/i, "")
    .trim();

  // Handle equations like "2x+5=13" -> solve for x, graph as vertical line x=4
  // or treat as y=2x+5 (ignore the =13 part and graph the left side)
  const equalsMatch = t.match(/^([^=]+)=([^=]+)$/);
  if (equalsMatch) {
    const lhs = equalsMatch[1].trim().replace(/\s+/g,"");
    const rhs = equalsMatch[2].trim().replace(/\s+/g,"");
    // If lhs has x and rhs is a number, graph y=lhs
    if (/x/.test(lhs) && /^-?[0-9.]+$/.test(rhs)) {
      const test = safeEval(lhs, 1);
      if (isFinite(test)) return "y=" + lhs;
    }
    // If it's y=something
    if (lhs === "y") {
      const test = safeEval(rhs, 1);
      if (isFinite(test)) return "y=" + rhs;
    }
  }

  const patterns = [
    /f\s*\(\s*x\s*\)\s*=\s*(.+)/i,
    /y\s*=\s*(.+)/i,
    /graph\s+(.+)/i,
    /plot\s+(.+)/i,
    /(.+)/,
  ];

  for (const p of patterns) {
    const m = t.match(p);
    if (m) {
      let expr = m[1].trim()
        .replace(/f\s*\(\s*x\s*\)/gi, "x")
        .replace(/\s+/g, "")
        .replace(/=[^=]*$/, ""); // strip =number at end
      if (expr.length > 0 && expr.length < 120 && /[x0-9]/.test(expr)) {
        const test = safeEval(expr, 1);
        const test2 = safeEval(expr, 0);
        if (isFinite(test) || isFinite(test2)) {
          return "y=" + expr;
        }
      }
    }
  }
  return null;
}

// ─────────────────────────────────────────────────────────────
// Shared canvas graph renderer used by both ChatGraph & QuizGraph
// ─────────────────────────────────────────────────────────────
function drawGraph(canvas, expr, darkMode, opts = {}) {
  // Scale canvas for device pixel ratio (fixes blank graphs on retina/iPhone)
  const dpr = window.devicePixelRatio || 1;
  const cssW = canvas.offsetWidth || canvas.width;
  const cssH = canvas.offsetHeight || canvas.height;
  canvas.width = cssW * dpr;
  canvas.height = cssH * dpr;
  const ctx = canvas.getContext("2d");
  ctx.scale(dpr, dpr);
  const W = cssW;
  const H = cssH;

  const bg        = darkMode ? "#0f1729" : "#ffffff";
  const gridColor = darkMode ? "#1e2d4a" : "#f0f4f8";
  const axisColor = darkMode ? "#475569" : "#94a3b8";
  const lineColor = opts.lineColor || "#3b82f6";
  const labelColor = darkMode ? "#64748b" : "#94a3b8";

  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  const { xMin, xMax, yMin, yMax } = computeGraphBounds(expr, opts.xMin || -10, opts.xMax || 10);

  const toX = x => ((x - xMin) / (xMax - xMin)) * W;
  const toY = y => H - ((y - yMin) / (yMax - yMin)) * H;

  // ── Grid ──
  ctx.strokeStyle = gridColor;
  ctx.lineWidth = 1;
  const xRange = xMax - xMin;
  const yRange = yMax - yMin;
  const xStep = xRange <= 12 ? 1 : xRange <= 30 ? 2 : xRange <= 60 ? 5 : 10;
  const yStep = yRange <= 12 ? 1 : yRange <= 30 ? 2 : yRange <= 60 ? 5 : 10;

  for (let gx = Math.ceil(xMin / xStep) * xStep; gx <= xMax; gx += xStep) {
    ctx.beginPath(); ctx.moveTo(toX(gx), 0); ctx.lineTo(toX(gx), H); ctx.stroke();
  }
  for (let gy = Math.ceil(yMin / yStep) * yStep; gy <= yMax; gy += yStep) {
    ctx.beginPath(); ctx.moveTo(0, toY(gy)); ctx.lineTo(W, toY(gy)); ctx.stroke();
  }

  // ── Axes ──
  ctx.strokeStyle = axisColor;
  ctx.lineWidth = 1.5;
  if (xMin < 0 && xMax > 0) {
    ctx.beginPath(); ctx.moveTo(toX(0), 0); ctx.lineTo(toX(0), H); ctx.stroke();
  }
  if (yMin < 0 && yMax > 0) {
    ctx.beginPath(); ctx.moveTo(0, toY(0)); ctx.lineTo(W, toY(0)); ctx.stroke();
  }

  // ── Axis labels ──
  ctx.fillStyle = labelColor;
  ctx.font = "11px system-ui, sans-serif";
  ctx.textAlign = "center";
  for (let gx = Math.ceil(xMin / xStep) * xStep; gx <= xMax; gx += xStep) {
    if (Math.abs(gx) < 0.001) continue;
    const py = Math.min(Math.max(toY(0) + 14, 14), H - 4);
    ctx.fillText(Number(gx.toFixed(1)), toX(gx), py);
  }
  ctx.textAlign = "right";
  for (let gy = Math.ceil(yMin / yStep) * yStep; gy <= yMax; gy += yStep) {
    if (Math.abs(gy) < 0.001) continue;
    const px = Math.min(Math.max(toX(0) - 5, 22), W - 4);
    ctx.fillText(Number(gy.toFixed(1)), px, toY(gy) + 4);
  }

  // ── Axis letter labels ──
  ctx.fillStyle = axisColor;
  ctx.font = "bold 12px system-ui";
  ctx.textAlign = "center";
  ctx.fillText("x", W - 6, toY(0) - 6 < 14 ? 14 : toY(0) - 6);
  ctx.fillText("y", toX(0) + 12 > W - 4 ? W - 4 : toX(0) + 12, 12);

  // ── Plot the function ──
  ctx.strokeStyle = lineColor;
  ctx.lineWidth = 3;
  ctx.beginPath();
  let started = false;
  let prevY = NaN;
  const steps = W * 4;  // high resolution for smooth curves

  for (let i = 0; i <= steps; i++) {
    const x = xMin + (i / steps) * (xMax - xMin);
    const y = safeEval(expr, x);

    // Skip discontinuities (asymptotes etc)
    const jump = isFinite(prevY) && isFinite(y) && Math.abs(y - prevY) > (yMax - yMin) * 0.6;
    if (!isFinite(y) || y < yMin - (yMax - yMin) || y > yMax + (yMax - yMin) || jump) {
      started = false;
      prevY = y;
      continue;
    }

    const cx = toX(x);
    const cy = toY(y);
    if (!started) { ctx.moveTo(cx, cy); started = true; }
    else ctx.lineTo(cx, cy);
    prevY = y;
  }
  ctx.stroke();
  ctx.shadowBlur = 0;

  // ── Function label ──
  const rawLabel = expr.replace(/^y=/i, "");
  const label = "y = " + rawLabel;
  ctx.fillStyle = lineColor;
  ctx.font = "bold 13px system-ui, sans-serif";
  ctx.textAlign = "left";
  ctx.shadowColor = bg;
  ctx.shadowBlur = 3;
  ctx.fillText(label, 8, 18);
  ctx.shadowBlur = 0;
}

function ChatGraph({ expr, darkMode }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !expr) return;
    drawGraph(canvas, expr, darkMode);
  }, [expr, darkMode]);

  return (
    <div style={{borderRadius: 12, overflow: "hidden", border: `1px solid ${darkMode ? "#1e2d4a" : "#e2e8f0"}`, marginTop: 8}}>
      <canvas ref={canvasRef} width={500} height={280} style={{width: "100%", height: 280, display: "block"}} />
    </div>
  );
}

function QuizGraph({ expr, darkMode }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !expr) return;
    drawGraph(canvas, expr, darkMode, { xMin: -8, xMax: 8 });
  }, [expr, darkMode]);

  return (
    <div style={{ marginBottom: 16, borderRadius: 12, overflow: "hidden", border: `1px solid ${darkMode ? "#1e2d4a" : "#e2e8f0"}` }}>
      <canvas ref={canvasRef} width={460} height={220} style={{ width: "100%", height: 220, display: "block" }} />
    </div>
  );
}

function getExamples(subject) {
  const map = {
    Math: ["Solve: 2x + 5 = 13", "Explain the quadratic formula", "What is a derivative?"],
    Science: ["How does photosynthesis work?", "Explain Newton's 3rd law", "What is DNA?"],
    History: ["Causes of World War I", "What was the Renaissance?", "The Civil Rights Movement"],
    English: ["Help me write a thesis statement", "What is a metaphor?", "Improve my essay intro"],
    Languages: ["Conjugate 'to be' in Spanish", "French greetings", "German sentence structure"],
    Coding: ["Explain a for loop in Python", "What is an API?", "Difference: == vs ==="],
    "Test Prep": ["SAT Math tips", "Make me a TEAS quiz", "ACT Science strategies"],
    "College Essays": ["How to start a Common App essay", "Show don't tell tips", "Review my opening paragraph"],
  };
  return map[subject] || ["Ask me anything!"];
}

function getStyles(dark) {
  const bg = dark ? "#080d1a" : "#f8fafc";
  const bg2 = dark ? "#0f1729" : "#ffffff";
  const bg3 = dark ? "#1e293b" : "#f1f5f9";
  const border = dark ? "#1e2d4a" : "#e2e8f0";
  const text = dark ? "#f1f5f9" : "#0f172a";
  const subtext = dark ? "#cbd5e1" : "#475569";
  const muted = dark ? "#64748b" : "#94a3b8";

  return {
    page: {minHeight: "100vh", background: bg, color: text, fontFamily: "'Segoe UI', system-ui, sans-serif", display: "flex", flexDirection: "column", alignItems: "center", padding: 20},
    homeWrap: {maxWidth: 480, width: "100%", textAlign: "center", paddingTop: 60},
    homeTitle: {fontSize: 26, fontWeight: 700, marginBottom: 10, color: text},
    homeSubtitle: {fontSize: 15, color: subtext, marginBottom: 24},
    subjectGrid: {display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center", marginBottom: 24},
    subjectPillBtn: {background: bg2, border: `1px solid ${border}`, borderRadius: 20, padding: "6px 14px", fontSize: 13, color: subtext, cursor: "pointer", transition: "all 0.15s"},
    primaryBtn: {width: "100%", padding: "14px 0", background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: "pointer", marginBottom: 16},
    toolsRow: {display: "flex", gap: 12, marginBottom: 16, width: "100%"},
    toolBtn: {flex: 1, background: bg2, border: `1px solid ${border}`, borderRadius: 14, padding: "14px 10px", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 4, color: text},
    ghostBtn: {width: "100%", padding: "12px 0", background: "transparent", color: subtext, border: `1px solid ${border}`, borderRadius: 12, fontSize: 15, cursor: "pointer", marginBottom: 20},
    planBadge: {fontSize: 14, color: muted, marginTop: 8},
    plansWrap: {maxWidth: 900, width: "100%", paddingTop: 30},
    plansTitle: {fontSize: 26, fontWeight: 800, textAlign: "center", marginBottom: 6, color: text},
    plansSub: {textAlign: "center", color: subtext, marginBottom: 28},
    plansGrid: {display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center"},
    planCard: {background: bg2, border: "2px solid", borderRadius: 16, padding: 24, width: 240, display: "flex", flexDirection: "column", alignItems: "center", gap: 8},
    planName: {fontWeight: 800, fontSize: 20},
    planPrice: {fontSize: 28, fontWeight: 800, color: text},
    planPeriod: {fontSize: 14, fontWeight: 400, color: subtext},
    featureList: {listStyle: "none", padding: 0, margin: "8px 0", textAlign: "left", width: "100%"},
    featureItem: {fontSize: 13, color: subtext, padding: "3px 0"},
    planBtn: {width: "100%", padding: "10px 0", border: "none", borderRadius: 8, color: "#fff", fontWeight: 700, fontSize: 14, marginTop: 8, transition: "opacity 0.2s"},
    upgradeWrap: {maxWidth: 480, width: "100%", textAlign: "center", paddingTop: 60, display: "flex", flexDirection: "column", alignItems: "center", gap: 14},
    upgradeTitle: {fontSize: 24, fontWeight: 800, color: text},
    upgradeReason: {color: subtext, fontSize: 15, maxWidth: 360},
    upgradePlans: {display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "center", margin: "12px 0"},
    upgradeCard: {background: bg2, border: "2px solid", borderRadius: 14, padding: 20, width: 190, display: "flex", flexDirection: "column", alignItems: "center", gap: 8},
    chatPage: {height: "100vh", width: "100%", maxWidth: "100%", margin: "0 auto", display: "flex", flexDirection: "column", background: bg, color: text, fontFamily: "'Segoe UI', system-ui, sans-serif"},
    chatHeader: {display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: `1px solid ${border}`, background: bg},
    headerCenter: {flex: 1, textAlign: "center"},
    planChip: {border: "1px solid", borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", background: "transparent"},
    subjectBar: {display: "flex", overflowX: "auto", gap: 8, padding: "10px 14px", borderBottom: `1px solid ${border}`, scrollbarWidth: "none"},
    subjectTab: {flexShrink: 0, padding: "6px 14px", border: "1px solid transparent", borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all 0.15s"},
    usageBar: {display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 16px", borderBottom: `1px solid ${border}`, background: bg},
    upgradeInlineBtn: {background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, padding: "4px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer"},
    clearBtn: {background: "transparent", color: muted, border: `1px solid ${border}`, borderRadius: 8, padding: "4px 10px", fontSize: 12, cursor: "pointer"},
    messagesArea: {flex: 1, overflowY: "auto", padding: "16px calc(50% - 400px)", display: "flex", flexDirection: "column", gap: 12, background: bg},
    emptyState: {textAlign: "center", padding: "40px 20px", color: muted},
    emptyTitle: {fontSize: 18, fontWeight: 700, color: subtext, marginBottom: 8},
    emptySubtitle: {fontSize: 14, marginBottom: 20, color: muted},
    examplesRow: {display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center"},
    exampleChip: {background: bg2, border: `1px solid ${border}`, borderRadius: 20, padding: "8px 16px", fontSize: 13, color: subtext, cursor: "pointer", transition: "all 0.15s"},
    msgWrap: {display: "flex", alignItems: "center", gap: 8},
    avatarAI: {fontSize: 22, flexShrink: 0},
    avatarUser: {fontSize: 22, flexShrink: 0},
    bubble: {maxWidth: "80%", padding: "10px 14px", lineHeight: 1.6},
    msgSubject: {fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#60a5fa", marginBottom: 4},
    msgText: {margin: 0, fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "inherit", lineHeight: 1.9},
    aiBubble: {background: bg3, color: text},
    typing: {display: "flex", gap: 4, padding: "4px 0", alignItems: "center"},
    imagePreviewBar: {display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", borderTop: `1px solid ${border}`, background: bg},
    inputArea: {display: "flex", gap: 8, padding: "10px 14px 14px", borderTop: `1px solid ${border}`, background: bg, alignItems: "flex-end"},
    iconBtn: {background: dark ? "#3b82f6" : "#f1f5f9", border: "none", borderRadius: 10, fontSize: 18, padding: "8px 10px", cursor: "pointer", flexShrink: 0},
    textarea: {flex: 1, background: bg2, border: `1px solid ${border}`, borderRadius: 24, color: text, fontSize: 14, padding: "10px 18px", resize: "none", outline: "none", fontFamily: "inherit", lineHeight: 1.5},
    sendBtn: {background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff", border: "none", borderRadius: 24, padding: "0 22px", fontWeight: 700, fontSize: 14, cursor: "pointer", transition: "opacity 0.2s", height: 44, flexShrink: 0},
    backBtn: {background: "transparent", border: "none", color: subtext, fontSize: 14, cursor: "pointer", padding: 4},
    toast: {position: "fixed", bottom: 30, left: "50%", transform: "translateX(-50%)", background: bg2, border: `1px solid ${border}`, color: text, padding: "10px 20px", borderRadius: 12, fontSize: 14, fontWeight: 600, zIndex: 999, boxShadow: "0 4px 20px rgba(0,0,0,0.4)"},
    toolTextarea: {width: "100%", background: bg2, border: `1px solid ${border}`, borderRadius: 12, color: text, fontSize: 14, padding: "12px 14px", resize: "vertical", outline: "none", fontFamily: "inherit", lineHeight: 1.6, marginBottom: 12, boxSizing: "border-box"},
    resultBox: {background: bg2, border: `1px solid ${border}`, borderRadius: 14, padding: 20, marginTop: 16},
    resultLabel: {fontSize: 13, fontWeight: 700, color: "#3b82f6", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em"},
    resultText: {margin: 0, fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "inherit", color: text, lineHeight: 1.8},
    copyBtn: {marginTop: 12, background: "transparent", color: dark ? "#94a3b8" : "#64748b", border: `1px solid ${border}`, borderRadius: 8, padding: "8px 18px", fontSize: 13, cursor: "pointer", fontWeight: 500, letterSpacing: "0.02em"},
    themeBtn: {background: "transparent", border: `1px solid ${border}`, borderRadius: 20, padding: "4px 10px", fontSize: 16, cursor: "pointer", color: text},
  };
}

function GrapherScreen({ darkMode, onBack, toggleDarkMode, toastMsg }) {
  const canvasRef = useRef(null);
  const [equations, setEquations] = useState([
    { expr: "y=x^2", color: "#3b82f6", visible: true },
  ]);
  const [inputVal, setInputVal] = useState("");
  const [error, setError] = useState("");

  const bg = darkMode ? "#080d1a" : "#f8fafc";
  const card = darkMode ? "#0f1729" : "#ffffff";
  const border = darkMode ? "#1e2d4a" : "#e2e8f0";
  const text = darkMode ? "#f1f5f9" : "#0f172a";
  const muted = darkMode ? "#94a3b8" : "#64748b";

  const COLORS = ["#3b82f6","#f59e0b","#10b981","#ef4444","#a855f7","#ec4899","#06b6d4","#84cc16"];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width; const H = canvas.height;

    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = darkMode ? "#0f1729" : "#ffffff";
    ctx.fillRect(0, 0, W, H);

    const xMin = -10, xMax = 10;

    // Auto y range from all visible equations
    let yVals = [];
    equations.filter(eq => eq.visible).forEach(eq => {
      for (let i = 0; i <= 400; i++) {
        const x = xMin + (i / 400) * (xMax - xMin);
        const v = safeEval(eq.expr, x);
        if (isFinite(v)) yVals.push(v);
      }
    });
    let yMin = -8, yMax = 8;
    if (yVals.length > 0) {
      const lo = Math.min(...yVals), hi = Math.max(...yVals);
      const pad = Math.max((hi - lo) * 0.15, 1);
      yMin = lo - pad; yMax = hi + pad;
      if (yMax - yMin > 200) { yMin = -12; yMax = 12; }
      if (yMax - yMin < 2) { yMin -= 2; yMax += 2; }
    }

    const toX = x => ((x - xMin) / (xMax - xMin)) * W;
    const toY = y => H - ((y - yMin) / (yMax - yMin)) * H;

    const gridColor = darkMode ? "#1e2d4a" : "#f1f5f9";
    ctx.strokeStyle = gridColor; ctx.lineWidth = 1;
    const xStep = (xMax - xMin) <= 20 ? 1 : 2;
    const yRange = yMax - yMin;
    const yStep = yRange <= 20 ? 1 : yRange <= 50 ? 5 : 10;
    for (let x = Math.ceil(xMin); x <= xMax; x += xStep) {
      ctx.beginPath(); ctx.moveTo(toX(x), 0); ctx.lineTo(toX(x), H); ctx.stroke();
    }
    for (let y = Math.ceil(yMin/yStep)*yStep; y <= yMax; y += yStep) {
      ctx.beginPath(); ctx.moveTo(0, toY(y)); ctx.lineTo(W, toY(y)); ctx.stroke();
    }

    const axisColor = darkMode ? "#475569" : "#94a3b8";
    ctx.strokeStyle = axisColor; ctx.lineWidth = 1.5;
    if (yMin < 0 && yMax > 0) {
      ctx.beginPath(); ctx.moveTo(0, toY(0)); ctx.lineTo(W, toY(0)); ctx.stroke();
    }
    if (xMin < 0 && xMax > 0) {
      ctx.beginPath(); ctx.moveTo(toX(0), 0); ctx.lineTo(toX(0), H); ctx.stroke();
    }

    ctx.fillStyle = darkMode ? "#64748b" : "#94a3b8";
    ctx.font = "11px system-ui";
    ctx.textAlign = "center";
    for (let x = Math.ceil(xMin); x <= xMax; x += xStep) {
      if (x !== 0) {
        const py = Math.min(Math.max(toY(0) + 14, 14), H - 4);
        ctx.fillText(x, toX(x), py);
      }
    }
    ctx.textAlign = "right";
    for (let y = Math.ceil(yMin/yStep)*yStep; y <= yMax; y += yStep) {
      if (Math.abs(y) > 0.01) {
        const px = Math.min(Math.max(toX(0) - 4, 20), W - 4);
        ctx.fillText(Number(y.toFixed(1)), px, toY(y) + 4);
      }
    }

    equations.filter(eq => eq.visible).forEach(eq => {
      ctx.strokeStyle = eq.color;
      ctx.lineWidth = 2.5;
      ctx.shadowColor = eq.color;
      ctx.shadowBlur = 4;
      ctx.beginPath();
      let started = false;
      let prevY = NaN;
      const steps = W * 4;
      for (let i = 0; i <= steps; i++) {
        const x = xMin + (i/steps)*(xMax-xMin);
        const y = safeEval(eq.expr, x);
        const jump = isFinite(prevY) && isFinite(y) && Math.abs(y - prevY) > (yMax - yMin) * 0.6;
        if (!isFinite(y) || y < yMin - (yMax-yMin) || y > yMax + (yMax-yMin) || jump) {
          started = false; prevY = y; continue;
        }
        const cx = toX(x), cy = toY(y);
        if (!started) { ctx.moveTo(cx, cy); started = true; } else ctx.lineTo(cx, cy);
        prevY = y;
      }
      ctx.stroke();
      ctx.shadowBlur = 0;
    });
  }, [equations, darkMode]);

  function addEquation() {
    const raw = inputVal.trim();
    if (!raw) return;
    const expr = raw.startsWith("y=") || raw.startsWith("y =") ? raw.replace(/\s/g,"") : "y=" + raw.replace(/\s/g,"");
    const test0 = safeEval(expr, 0);
    const test1 = safeEval(expr, 1);
    if (!isFinite(test0) && !isFinite(test1)) {
      setError("Couldn't parse that equation. Try: y=x^2 or y=sin(x)");
      return;
    }
    setError("");
    const color = COLORS[equations.length % COLORS.length];
    setEquations(prev => [...prev, { expr, color, visible: true }]);
    setInputVal("");
  }

  function removeEquation(i) {
    setEquations(prev => prev.filter((_, idx) => idx !== i));
  }

  function toggleEquation(i) {
    setEquations(prev => prev.map((eq, idx) => idx === i ? {...eq, visible: !eq.visible} : eq));
  }

  function insertSnippet(s) {
    setInputVal(prev => prev + s);
  }

  const snippets = ["x^2", "x^3", "sqrt(x)", "sin(x)", "cos(x)", "tan(x)", "ln(x)", "e^x", "abs(x)", "1/x", "2x+1", "x^2-4"];

  return (
    <div style={{minHeight: "100vh", background: bg, color: text, fontFamily: "'Segoe UI', system-ui, sans-serif", display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 12px"}}>
      <div style={{maxWidth: 680, width: "100%"}}>
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16}}>
          <button style={{background: "transparent", border: "none", color: muted, fontSize: 14, cursor: "pointer", padding: 4}} onClick={onBack}>← Home</button>
          <span style={{fontWeight: 700, fontSize: 16, color: text}}>📈 Graphing Calculator</span>
          <button style={{background: "transparent", border: `1px solid ${border}`, borderRadius: 20, padding: "4px 10px", fontSize: 16, cursor: "pointer", color: text}} onClick={toggleDarkMode}>{darkMode ? "☀️" : "🌙"}</button>
        </div>

        <div style={{borderRadius: 16, overflow: "hidden", border: `1px solid ${border}`, marginBottom: 16, background: darkMode ? "#0f1729" : "#fff"}}>
          <canvas ref={canvasRef} width={660} height={360} style={{width: "100%", height: 300, display: "block"}} />
        </div>

        {equations.length > 0 && (
          <div style={{background: card, border: `1px solid ${border}`, borderRadius: 14, padding: "10px 14px", marginBottom: 14}}>
            {equations.map((eq, i) => (
              <div key={i} style={{display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: i < equations.length-1 ? `1px solid ${border}` : "none"}}>
                <div onClick={() => toggleEquation(i)} style={{width: 14, height: 14, borderRadius: "50%", background: eq.visible ? eq.color : border, cursor: "pointer", flexShrink: 0, border: `2px solid ${eq.color}`}} />
                <span style={{fontSize: 13, color: eq.visible ? text : muted, flex: 1, fontFamily: "monospace"}}>{eq.expr}</span>
                <button onClick={() => removeEquation(i)} style={{background: "transparent", border: "none", color: muted, cursor: "pointer", fontSize: 16, padding: "0 4px", lineHeight: 1}}>×</button>
              </div>
            ))}
          </div>
        )}

        <div style={{display: "flex", gap: 8, marginBottom: 10}}>
          <input
            value={inputVal}
            onChange={e => { setInputVal(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && addEquation()}
            placeholder="Type equation like x^2 or sin(x) or 2x+3"
            style={{flex: 1, background: card, border: `1px solid ${error ? "#dc2626" : border}`, borderRadius: 10, color: text, fontSize: 14, padding: "10px 14px", outline: "none", fontFamily: "monospace"}}
          />
          <button onClick={addEquation} style={{padding: "10px 20px", background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer", flexShrink: 0}}>
            Plot
          </button>
        </div>
        {error && <p style={{fontSize: 12, color: "#dc2626", marginBottom: 10}}>{error}</p>}

        <div style={{marginBottom: 16}}>
          <p style={{fontSize: 12, color: muted, marginBottom: 8, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em"}}>Quick insert</p>
          <div style={{display: "flex", flexWrap: "wrap", gap: 6}}>
            {snippets.map(s => (
              <button key={s} onClick={() => insertSnippet(s)} style={{padding: "5px 12px", background: card, border: `1px solid ${border}`, borderRadius: 20, fontSize: 12, color: muted, cursor: "pointer", fontFamily: "monospace"}}>
                {s}
              </button>
            ))}
          </div>
        </div>

        <div style={{background: card, border: `1px solid ${border}`, borderRadius: 12, padding: "12px 16px"}}>
          <p style={{fontSize: 12, fontWeight: 700, color: "#3b82f6", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em"}}>Tips</p>
          <p style={{fontSize: 12, color: muted, lineHeight: 1.8, margin: 0}}>
            Use ^ for exponents — x^2, x^3<br/>
            Use * for multiplication — 2*x or just 2x<br/>
            Functions: sin(x), cos(x), tan(x), sqrt(x), ln(x), log(x), abs(x)<br/>
            Constants: pi, e<br/>
            Plot multiple equations to compare them — each gets its own color<br/>
            Click the colored dot next to an equation to hide/show it
          </p>
        </div>
      </div>
      {toastMsg && <div style={{position:"fixed",bottom:30,left:"50%",transform:"translateX(-50%)",background:card,border:`1px solid ${border}`,color:text,padding:"10px 20px",borderRadius:12,fontSize:14,fontWeight:600,zIndex:999}}>{toastMsg}</div>}
    </div>
  );
}

function SettingsScreen({ darkMode, toggleDarkMode, user, plan, displayName, setDisplayName, onBack, onLogout, onManagePlan, showToast, db, toastMsg }) {
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(displayName || "");
  const [changingPassword, setChangingPassword] = useState(false);
  const nameInputRef = useRef(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingPassword, setPendingPassword] = useState("");

  const bg = darkMode ? "#080d1a" : "#f8fafc";
  const card = darkMode ? "#0f1729" : "#ffffff";
  const border = darkMode ? "#1e2d4a" : "#e2e8f0";
  const text = darkMode ? "#f1f5f9" : "#0f172a";
  const muted = darkMode ? "#94a3b8" : "#64748b";

  const PLAN_META = {
    free: { name: "Free", color: "#94a3b8", badge: "🎒" },
    scholar: { name: "Scholar", color: "#3b82f6", badge: "⭐" },
    genius: { name: "Genius", color: "#f59e0b", badge: "🏆" },
  };

  const inputStyle = {
    width: "100%", background: bg, border: `1px solid ${border}`,
    borderRadius: 10, color: text, fontSize: 14, padding: "10px 14px",
    outline: "none", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 10,
  };

  async function saveName() {
    const val = nameInputRef.current ? nameInputRef.current.value.trim() : nameInput.trim();
    if (!val) return;
    setLoading(true);
    try {
      await updateDoc(doc(db, "users", user.uid), { name: val });
      setDisplayName(val.split(" ")[0]);
      setNameInput(val);
      setEditingName(false);
      showToast("Name updated!");
    } catch { showToast("Error updating name."); }
    setLoading(false);
  }

  async function changePassword() {
    if (!currentPassword || !newPassword || !confirmPassword) { showToast("Please fill in all fields."); return; }
    if (newPassword !== confirmPassword) { showToast("New passwords don't match."); return; }
    if (newPassword.length < 6) { showToast("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setChangingPassword(false);
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
      showToast("Password updated successfully!");
    } catch (e) {
      showToast(e.code === "auth/wrong-password" ? "Current password is incorrect." : "Error updating password.");
    }
    setLoading(false);
  }

  async function deleteAccount() {
    if (!deletePassword) { showToast("Please enter your password to confirm."); return; }
    setLoading(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, deletePassword);
      await reauthenticateWithCredential(user, credential);
      await deleteDoc(doc(db, "users", user.uid));
      await deleteUser(user);
      showToast("Account deleted.");
      onBack();
    } catch (e) {
      showToast(e.code === "auth/wrong-password" ? "Incorrect password." : "Error deleting account.");
    }
    setLoading(false);
  }

  const Section = ({ title, children }) => (
    <div style={{ marginBottom: 20 }}>
      <p style={{ fontSize: 11, fontWeight: 700, color: muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>{title}</p>
      <div style={{ background: card, border: `1px solid ${border}`, borderRadius: 14, overflow: "hidden" }}>
        {children}
      </div>
    </div>
  );

  const Row = ({ label, value, onClick, danger, icon }) => (
    <div onClick={onClick} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: `1px solid ${border}`, cursor: onClick ? "pointer" : "default" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {icon && <span style={{ fontSize: 16 }}>{icon}</span>}
        <span style={{ fontSize: 14, color: danger ? "#ef4444" : text }}>{label}</span>
      </div>
      {value && <span style={{ fontSize: 13, color: muted }}>{value}</span>}
      {onClick && <span style={{ fontSize: 12, color: muted }}>›</span>}
    </div>
  );

  const isGoogleUser = user?.providerData?.[0]?.providerId === "google.com";

  return (
    <div style={{ minHeight: "100vh", background: bg, color: text, fontFamily: "'Segoe UI', system-ui, sans-serif", padding: "16px 16px" }}>
      <div style={{ maxWidth: 500, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <button onClick={onBack} style={{ background: "transparent", border: "none", color: muted, fontSize: 14, cursor: "pointer", padding: 0 }}>← Back</button>
          <span style={{ fontWeight: 700, fontSize: 16, color: text }}>Settings</span>
          <div style={{ width: 40 }} />
        </div>

        <Section title="Profile">
          <div style={{ padding: "16px 16px", borderBottom: `1px solid ${border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: editingName ? 12 : 0 }}>
              <div style={{ width: 44, height: 44, borderRadius: "50%", background: "linear-gradient(135deg, #3b82f6, #2563eb)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "#fff", flexShrink: 0 }}>
                {displayName ? displayName[0].toUpperCase() : "🎓"}
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 15, fontWeight: 600, color: text, margin: 0 }}>{displayName || "No name set"}</p>
                <p style={{ fontSize: 12, color: muted, margin: 0 }}>{user?.email}</p>
              </div>
              <button onClick={() => { setEditingName(!editingName); setNameInput(displayName || ""); }} style={{ fontSize: 12, color: "#3b82f6", background: "transparent", border: "none", cursor: "pointer" }}>
                {editingName ? "Cancel" : "Edit"}
              </button>
            </div>
            {editingName && (
              <div>
                <input
                  ref={nameInputRef}
                  defaultValue={nameInput}
                  placeholder="Your first name"
                  style={inputStyle}
                  onKeyDown={e => e.key === "Enter" && saveName()}
                  autoFocus
                />
                <button onClick={saveName} disabled={loading} style={{ width: "100%", padding: "10px 0", background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  {loading ? "Saving..." : "Save Name"}
                </button>
              </div>
            )}
          </div>
          <Row label="Plan" value={`${PLAN_META[plan]?.badge} ${PLAN_META[plan]?.name}`} onClick={onManagePlan} icon="📋" />
          {(plan === "scholar" || plan === "genius") && (
            <>
            <div style={{ padding: "8px 16px", fontSize: 12, color: muted, background: darkMode ? "#0d1829" : "#f8fafc", borderBottom: `1px solid ${border}` }}>
              Your subscription stays active until the end of your current billing period.
            </div>
            <Row label="Cancel Subscription" onClick={async () => {
              setLoading(true);
              try {
                const response = await fetch("https://createcheckoutsession-mo6g2pop4q-uc.a.run.app/customerPortal", {
                  method: "POST",
                  headers: {"Content-Type": "application/json"},
                  body: JSON.stringify({email: user.email}),
                });
                const data = await response.json();
                if (data.url) window.location.href = data.url;
                else showToast("Could not open billing portal. Please contact support.");
              } catch { showToast("Error. Please contact support at scholarsensai.com"); }
              setLoading(false);
            }} icon="❌" danger />
            </>
          )}
        </Section>

        {!isGoogleUser && (
          <Section title="Security">
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: changingPassword ? 12 : 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 16 }}>🔒</span>
                  <span style={{ fontSize: 14, color: text }}>Change Password</span>
                </div>
                <button onClick={() => setChangingPassword(!changingPassword)} style={{ fontSize: 12, color: "#3b82f6", background: "transparent", border: "none", cursor: "pointer" }}>
                  {changingPassword ? "Cancel" : "Change"}
                </button>
              </div>
              {changingPassword && (
                <div style={{ marginTop: 12 }}>
                  <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Current password" style={inputStyle} />
                  <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password" style={inputStyle} />
                  <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Confirm new password" style={{...inputStyle, marginBottom: 10}} onKeyDown={e => e.key === "Enter" && changePassword()} />
                  <button onClick={changePassword} disabled={loading} style={{ width: "100%", padding: "10px 0", background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                    {loading ? "Updating..." : "Update Password"}
                  </button>
                </div>
              )}
            </div>
          </Section>
        )}

        <Section title="Account">
          <Row label="Sign out" onClick={onLogout} icon="👋" />
          <div style={{ padding: "14px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: deleting ? 12 : 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 16 }}>🗑️</span>
                <span style={{ fontSize: 14, color: "#ef4444" }}>Delete Account</span>
              </div>
              <button onClick={() => setDeleting(!deleting)} style={{ fontSize: 12, color: "#ef4444", background: "transparent", border: "none", cursor: "pointer" }}>
                {deleting ? "Cancel" : "Delete"}
              </button>
            </div>
            {deleting && (
              <div style={{ marginTop: 12 }}>
                <p style={{ fontSize: 12, color: muted, marginBottom: 10 }}>This will permanently delete your account and all data. This cannot be undone.</p>
                {!isGoogleUser && <input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} placeholder="Enter password to confirm" style={inputStyle} />}
                <button onClick={deleteAccount} disabled={loading} style={{ width: "100%", padding: "10px 0", background: "#ef4444", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
                  {loading ? "Deleting..." : "Permanently Delete Account"}
                </button>
              </div>
            )}
          </div>
        </Section>

        <p style={{ textAlign: "center", fontSize: 11, color: muted, marginTop: 8 }}>ScholarSensAi v1.0 • scholarsensai.com</p>
      </div>
      {toastMsg && <div style={{ position: "fixed", bottom: 30, left: "50%", transform: "translateX(-50%)", background: card, border: `1px solid ${border}`, color: text, padding: "10px 20px", borderRadius: 12, fontSize: 14, fontWeight: 600, zIndex: 999 }}>{toastMsg}</div>}
    </div>
  );
}

function LoginScreen({ darkMode, onSuccess }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const bg = darkMode ? "#080d1a" : "#f8fafc";
  const card = darkMode ? "#0f1729" : "#ffffff";
  const border = darkMode ? "#1e2d4a" : "#e2e8f0";
  const text = darkMode ? "#f1f5f9" : "#0f172a";
  const muted = darkMode ? "#94a3b8" : "#64748b";

  async function handleEmailAuth() {
    if (!email.trim() || !password.trim()) { setError("Please fill in all fields."); return; }
    if (mode === "signup" && !name.trim()) { setError("Please enter your name."); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true); setError("");
    try {
      if (mode === "login") {
        await signInWithEmailAndPassword(auth, email, password);
        onSuccess();
      } else {
        // Check if this email already exists (e.g. signed up with Google)
        const methods = await fetchSignInMethodsForEmail(auth, email);
        if (methods.includes("google.com") && !methods.includes("password")) {
          // Email exists via Google — sign them in with Google then link email/password to it
          setPendingPassword(password);
          setSuccess("This email is linked to your Google account. Tap 'Continue with Google' below to link your password to it. After that you can use either method to sign in.");
          setLoading(false);
          return;
        }
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await setDoc(doc(db, "users", cred.user.uid), {
          name, email, plan: "free", createdAt: Date.now(),
        });
        onSuccess();
      }
    } catch (e) {
      const errorMap = {
        "auth/user-not-found": "No account found with that email.",
        "auth/wrong-password": "Incorrect password.",
        "auth/invalid-credential": "Incorrect email or password.",
        "auth/email-already-in-use": "An account with this email already exists. Try signing in instead.",
        "auth/invalid-email": "Please enter a valid email address.",
        "auth/too-many-requests": "Too many failed attempts. Please wait a few minutes and try again.",
        "auth/user-disabled": "This account has been disabled. Please contact support.",
        "auth/weak-password": "Password must be at least 6 characters.",
        "auth/network-request-failed": "Network error. Please check your connection and try again.",
        "auth/requires-recent-login": "Please sign out and sign back in to continue.",
      };
      setError(errorMap[e.code] || "Something went wrong. Please try again.");
    }
    setLoading(false);
  }

  async function handleReset() {
    if (!email.trim()) { setError("Enter your email address first."); return; }
    setLoading(true); setError(""); setSuccess("");
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess("Password reset email sent! Check your inbox.");
    } catch (e) {
      setError(e.code === "auth/user-not-found" ? "No account found with that email." :
        e.code === "auth/invalid-email" ? "Please enter a valid email address." :
        "Something went wrong. Please try again.");
    }
    setLoading(false);
  }

  async function handleGoogle() {
    setLoading(true); setError("");
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      const userRef = doc(db, "users", cred.user.uid);
      const snap = await getDoc(userRef);
      if (!snap.exists()) {
        await setDoc(userRef, {
          name: cred.user.displayName || "",
          email: cred.user.email,
          plan: "free",
          createdAt: Date.now(),
        });
      }
      onSuccess();
    } catch(e) {
      if (e.code !== "auth/popup-closed-by-user") {
        setError("Google sign-in failed. Please try again.");
      }
    }
    setLoading(false);
  }

  const inputStyle = {
    width: "100%", background: card, border: `1px solid ${border}`,
    borderRadius: 12, color: text, fontSize: 14, padding: "12px 16px",
    outline: "none", fontFamily: "inherit", boxSizing: "border-box", marginBottom: 12,
  };

  return (
    <div style={{ minHeight: "100vh", background: bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ maxWidth: 400, width: "100%", background: card, border: `1px solid ${border}`, borderRadius: 20, padding: 32 }}>
        <button onClick={onSuccess} style={{ background: "transparent", border: "none", color: muted, fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 16, display: "block" }}>
          ← Back
        </button>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🎓</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: text, margin: 0 }}>
            {mode === "login" ? "Welcome back" : "Create account"}
          </h2>
          <p style={{ fontSize: 14, color: muted, marginTop: 6 }}>
            {mode === "login" ? "Sign in to ScholarSensAi" : "Join ScholarSensAi"}
          </p>
        </div>

        {error && (
          <div style={{ background: darkMode ? "#1a0a0a" : "#fef2f2", border: "1px solid #dc2626", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#dc2626", marginBottom: 16 }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ background: darkMode ? "#0a1a0a" : "#f0fdf4", border: "1px solid #16a34a", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#16a34a", marginBottom: 16 }}>
            {success}
          </div>
        )}

        {mode === "signup" && (
          <input type="text" placeholder="Your name" value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
        )}
        <input type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
        <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleEmailAuth()}
          style={{...inputStyle, marginBottom: 6}} />
        {mode === "login" && (
          <p style={{ textAlign: "right", fontSize: 12, color: "#3b82f6", cursor: "pointer", marginBottom: 16, marginTop: 0 }}
            onClick={handleReset}>
            Forgot password?
          </p>
        )}

        <button onClick={handleEmailAuth} disabled={loading} style={{ width: "100%", padding: "13px 0", background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: "pointer", marginBottom: 12, opacity: loading ? 0.7 : 1 }}>
          {loading ? "Please wait..." : mode === "login" ? "Sign in" : "Create account"}
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1, height: 1, background: border }} />
          <span style={{ fontSize: 12, color: muted }}>or</span>
          <div style={{ flex: 1, height: 1, background: border }} />
        </div>

        <button onClick={handleGoogle} disabled={loading} style={{ width: "100%", padding: "12px 0", background: card, border: `1px solid ${border}`, borderRadius: 12, fontSize: 14, fontWeight: 600, cursor: "pointer", color: text, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
          <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          Continue with Google
        </button>

        <p style={{ textAlign: "center", fontSize: 13, color: muted, marginTop: 20, marginBottom: 0 }}>
          {mode === "login" ? "Don't have an account? " : "Already have an account? "}
          <span onClick={() => { setMode(mode === "login" ? "signup" : "login"); setError(""); }} style={{ color: "#3b82f6", cursor: "pointer", fontWeight: 600 }}>
            {mode === "login" ? "Sign up" : "Sign in"}
          </span>
        </p>
      </div>
    </div>
  );
}

function QuizMode({ subject, darkMode, onExit }) {
  const [stage, setStage] = useState("config");
  const [difficulty, setDifficulty] = useState("medium");
  const [topicOverride, setTopicOverride] = useState("");
  const [questionCount, setQuestionCount] = useState(5);
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answered, setAnswered] = useState(false);
  const [score, setScore] = useState(0);
  const [wrongAnswers, setWrongAnswers] = useState([]);
  const [error, setError] = useState("");

  const bg = darkMode ? "#080d1a" : "#f8fafc";
  const card = darkMode ? "#0f1729" : "#ffffff";
  const border = darkMode ? "#1e2d4a" : "#e2e8f0";
  const text = darkMode ? "#f1f5f9" : "#0f172a";
  const muted = darkMode ? "#94a3b8" : "#64748b";
  const accent = "#3b82f6";

  function buildSingleQuestionPrompt(existingQuestions, forcedTopicIndex) {
    const difficultyMap = {
      easy: "beginner level, foundational concepts",
      medium: "intermediate level, requires real understanding",
      hard: "advanced level, challenging and nuanced",
    };
    const diffDesc = difficultyMap[difficulty];

    const mathTopics = ["algebra (solving equations, inequalities)", "geometry (area, volume, angles, triangles)", "arithmetic (fractions, decimals, percentages, ratios)", "word problems (rate, distance, time, mixtures)", "statistics (mean, median, mode, probability, data interpretation)", "exponents and scientific notation", "graph reading: linear functions (slope, y-intercept, identify equation from graph)", "graph reading: quadratic functions (vertex, roots, parabola shape)", "graph reading: calculus (identify where derivative is zero, increasing/decreasing intervals, concavity)", "graph reading: trigonometry (identify sine/cosine/tangent from graph, period, amplitude)", "proportions and unit conversions", "calculus: derivatives (power rule, chain rule, product rule, quotient rule)", "calculus: integrals (basic integration, u-substitution, definite integrals)", "calculus: limits (limit definition, L'Hopital's rule, continuity)", "calculus: applications (related rates, optimization, area under a curve)", "precalculus (trigonometry, unit circle, sine/cosine/tangent, logarithms and exponentials)"];
    const scienceTopics = ["human anatomy (skeletal, muscular, cardiovascular systems)", "human physiology (kidneys, lungs, liver, digestion)", "cell biology (mitosis, meiosis, organelles, DNA)", "genetics (Punnett squares, dominant/recessive traits)", "chemistry (periodic table, reactions, acids/bases, bonds)", "physics (force, motion, energy, electricity, waves)", "microbiology (bacteria, viruses, immune response)", "ecology (food chains, ecosystems, energy flow)"];
    const teasSections = ["reading comprehension (give a 3-4 sentence passage then ask about main idea, inference, or author purpose)", "English grammar (subject-verb agreement, punctuation, sentence structure, word choice)", "math (dosage calculation, ratios, percentages, unit conversion for nursing)", "anatomy and physiology (body systems, organ functions, medical terminology)", "science (cell biology, genetics, chemistry basics, scientific reasoning)"];
    const historyTopics = ["US History (colonial era, Revolution, Civil War, Reconstruction, WWI, WWII, Civil Rights)", "World History (ancient civilizations, Renaissance, Industrial Revolution, Cold War, globalization)", "AP US History themes (causation, continuity and change, comparison)", "AP World History themes (trade routes, empire building, revolutions)", "geography (physical features, climate, political boundaries)", "government and civics (Constitution, branches of government, amendments)"];
    const englishTopics = ["grammar rules (comma usage, semicolons, apostrophes, subject-verb agreement)", "vocabulary in context (choose the best word for the sentence)", "literary devices (metaphor, simile, foreshadowing, irony, symbolism)", "reading comprehension (give a short passage, ask about tone, theme, or main idea)", "sentence structure (run-ons, fragments, parallel structure)", "parts of speech (nouns, verbs, adjectives, adverbs, prepositions)"];
    const codingTopics = ["Python output questions (show code, ask what it prints)", "JavaScript output questions (show code, ask what it returns)", "data structures (arrays, objects, stacks, queues, linked lists)", "algorithms (sorting, searching, time complexity)", "debugging (find the bug in this code)", "functions and scope (closures, recursion, parameters)", "SQL queries (SELECT, JOIN, WHERE, GROUP BY)", "OOP concepts (classes, inheritance, polymorphism)"];
    const languageTopics = ["Spanish vocabulary (common words, cognates, false friends)", "Spanish verb conjugation (present, past, future, subjunctive)", "Spanish grammar (ser vs estar, por vs para, reflexive verbs)", "French vocabulary and greetings", "German grammar basics", "Mandarin tones and characters", "translation from English to target language", "reading comprehension in target language"];

    const topicPools = {
      Math: mathTopics,
      Science: scienceTopics,
      "Test Prep": teasSections,
      History: historyTopics,
      English: englishTopics,
      Coding: codingTopics,
      Languages: languageTopics,
    };

    const pool = topicPools[subject];
    const randomTopic = pool ? pool[forcedTopicIndex !== undefined ? forcedTopicIndex % pool.length : Math.floor(Math.random() * pool.length)] : null;

    const subjectInstructions = {
      Math: randomTopic && randomTopic.includes("graph reading") ?
        `Generate 1 math multiple choice question at ${diffDesc} about: ${randomTopic}. IMPORTANT: You MUST include a graph field in your JSON response with a simple equation like y=x^2 or y=2*x+1 or y=sin(x). The question should ask the student to read or interpret the graph. Do NOT describe the graph in words. Include the graph field and reference it as: Refer to the graph below.` :
        `Generate 1 math multiple choice question at ${diffDesc} specifically about: ${randomTopic}. Make it feel like a real standardized test question. Use realistic numbers. Wrong answers must reflect common mistakes students actually make.`,
      Science: `Generate 1 science multiple choice question at ${diffDesc} specifically about: ${randomTopic}. Make it feel like a real AP or college entrance exam question. Wrong answers should reflect common misconceptions.`,
      History: `Generate 1 history multiple choice question at ${diffDesc} specifically about: ${randomTopic}. Make it feel like a real AP History exam question. Test understanding of causes, effects, and significance, not just memorized facts.`,
      English: `Generate 1 English multiple choice question at ${diffDesc} specifically about: ${randomTopic}. Make it feel like a real SAT or ACT English question. If it is a reading comprehension question include a realistic 3-4 sentence passage.`,
      Languages: `Generate 1 language learning multiple choice question at ${diffDesc} specifically about: ${randomTopic}. Make it feel like a real language proficiency exam question.`,
      Coding: `Generate 1 coding multiple choice question at ${diffDesc} specifically about: ${randomTopic}. If it is an output question include realistic short code (3-6 lines). Wrong answers should reflect common bugs or misunderstandings.`,
      "Test Prep": `Generate 1 TEAS exam multiple choice question at ${diffDesc} specifically for this TEAS section: ${randomTopic}. The question must feel exactly like a real ATI TEAS 7 exam question. Use nursing and healthcare context where relevant. For reading comprehension include a realistic 3-4 sentence passage.`,
      "College Essays": `Generate 1 college application essay strategy question at ${diffDesc}. Cover one of these areas: Common App prompts and how to approach them, what admissions officers look for, how to show vs tell in personal statements, avoiding cliches, supplement essay strategies, how to write about activities or awards, or how to match your voice to your essay.`,
    };

    const subjectPrompt = subjectInstructions[subject] || `Generate 1 multiple choice question about ${subject} at ${diffDesc}. Make it feel like a real exam question.`;
    const avoidList = existingQuestions.length > 0 ? `\n\nIMPORTANT: Do NOT repeat or closely resemble any of these questions: ${existingQuestions.map(q => q.question).join(" | ")}` : "";

    const graphNote = subject === "Math" ? "\n\nCRITICAL GRAPH RULE: If the question mentions a graph, coordinate plane, curve, or function shape, you MUST include a graph field. The graph field value must be ONLY a math equation like: y=x^2 or y=2*x+1 or y=sin(x). NEVER write a sentence or description in the graph field. NEVER say imagine or consider. Just write the equation. If you cannot provide an equation, do not mention any graph in the question text at all." : "";

    return `${subjectPrompt}${avoidList}${graphNote}

Respond with ONLY this JSON (no markdown, no extra text):
{"category":"label","question":"question text","options":["A","B","C","D"],"correct":0,"explanation":"explanation"}

Rules: correct is 0-3. All options must be plausible. No "all of the above". Return ONLY the JSON.`;
  }

  async function fetchOneQuestion(existingQs, topicIndex) {
    const models = [
      "meta-llama/llama-3.3-70b-instruct",
      "mistralai/mistral-7b-instruct",
    ];
    let lastErr;
    for (const model of models) {
      try {
        const response = await fetch("https://us-central1-scholarx-6440c.cloudfunctions.net/aiProxy", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
                        "HTTP-Referer": "https://scholarsensai.com",
          },
          body: JSON.stringify({
            model,
            max_tokens: 800,
            temperature: 0.7,
            messages: [
              { role: "system", content: "You are a quiz question generator. Always respond with valid JSON only. No markdown, no explanation." },
              { role: "user", content: buildSingleQuestionPrompt(existingQs, topicIndex) }
            ],
          }),
        });
        const data = await response.json();
        const raw = data.choices?.[0]?.message?.content || "";
        // Extract JSON even if wrapped in markdown or extra text
        const jsonMatch = raw.match(/\{[\s\S]*\}/);
        if (!jsonMatch) throw new Error("No JSON found");
        const parsed = JSON.parse(jsonMatch[0]);
        if (!parsed.question || !parsed.options || parsed.correct === undefined) throw new Error("Invalid question format");
        return parsed;
      } catch(e) {
        lastErr = e;
        continue;
      }
    }
    throw lastErr;
  }

  function getShuffledIndices(count) {
    const indices = Array.from({length: count}, (_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
  }

  async function startQuiz() {
    setStage("loading");
    setError("");
    const topicIndices = getShuffledIndices(16);
    try {
      const q = await fetchOneQuestion([], topicIndices[0]);
      setQuestions([q]);
      setCurrent(0);
      setScore(0);
      setWrongAnswers([]);
      setSelected(null);
      setAnswered(false);
      setStage("quiz");
      let loaded = [q];
      for (let i = 1; i < questionCount; i++) {
        try {
          const next = await fetchOneQuestion(loaded, topicIndices[i]);
          loaded = [...loaded, next];
          setQuestions([...loaded]);
        } catch {}
      }
    } catch(e) {
      setError("Couldn't generate quiz. Error: " + (e?.message || "unknown") + ". Check your OpenRouter key has credits.");
      setStage("config");
    }
  }

  function handleSelect(idx) {
    if (answered) return;
    setSelected(idx);
    setAnswered(true);
    const q = questions[current];
    if (idx === q.correct) {
      setScore((s) => s + 1);
    } else {
      setWrongAnswers((w) => [...w, { ...q, chosen: idx }]);
    }
  }

  function handleNext() {
    if (current + 1 >= questions.length) {
      setStage("score");
    } else {
      setCurrent((c) => c + 1);
      setSelected(null);
      setAnswered(false);
    }
  }

  function retryQuiz() {
    setCurrent(0);
    setScore(0);
    setWrongAnswers([]);
    setSelected(null);
    setAnswered(false);
    setQuestions((qs) => [...qs].sort(() => Math.random() - 0.5));
    setStage("quiz");
  }

  function newQuiz(newDiff) {
    if (newDiff) setDifficulty(newDiff);
    setTopicOverride("");
    setQuestions([]);
    setCurrent(0);
    setScore(0);
    setWrongAnswers([]);
    setStage("config");
  }

  const q = questions[current];
  const pct = questions.length > 0 ? (current / questions.length) * 100 : 0;

  if (stage === "config") {
    const testPrepOptions = ["TEAS", "SAT", "ACT", "GRE", "GMAT", "LSAT", "NCLEX", "ASVAB", "Praxis", "GED", "AP Exam", "SOL", "STAAR"];
    return (
      <div style={{ flex: 1, overflowY: "auto", background: bg }}>
        <div style={{ padding: "20px 16px", maxWidth: 500, margin: "0 auto" }}>
          <button onClick={onExit} style={{ background: "transparent", border: "none", color: muted, cursor: "pointer", fontSize: 13, padding: 0, marginBottom: 24, display: "block" }}>
            ← Back to chat
          </button>
          <div style={{ marginBottom: 8 }}><span style={{ fontSize: 22 }}>🎯</span></div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: text, marginBottom: 4 }}>{subject} Quiz</h2>
          <p style={{ fontSize: 14, color: muted, marginBottom: 24 }}>Customize your quiz then hit start.</p>

          {subject === "Test Prep" && (
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: muted, display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Which test?</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {testPrepOptions.map((t) => (
                  <button key={t} onClick={() => setTopicOverride(topicOverride === t ? "" : t)} style={{ padding: "7px 14px", border: `1px solid ${topicOverride === t ? accent : border}`, borderRadius: 20, background: topicOverride === t ? (darkMode ? "#1e3a5f" : "#eff6ff") : card, color: topicOverride === t ? accent : muted, fontSize: 13, fontWeight: topicOverride === t ? 700 : 400, cursor: "pointer" }}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {subject !== "Test Prep" && (
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: muted, display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Topic (optional)</label>
              <input
                type="text"
                value={topicOverride}
                onChange={(e) => setTopicOverride(e.target.value)}
                placeholder={subject === "Math" ? "e.g. derivatives, geometry, statistics" : subject === "Science" ? "e.g. cell biology, anatomy" : subject === "History" ? "e.g. Civil War, World War II" : "e.g. specific topic"}
                style={{ width: "100%", background: card, border: `1px solid ${border}`, borderRadius: 10, color: text, fontSize: 14, padding: "10px 14px", outline: "none", fontFamily: "inherit", boxSizing: "border-box" }}
              />
            </div>
          )}

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: muted, display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Difficulty</label>
            <div style={{ display: "flex", gap: 8 }}>
              {["easy", "medium", "hard"].map((d) => (
                <button key={d} onClick={() => setDifficulty(d)} style={{ flex: 1, padding: "9px 0", border: `1px solid ${difficulty === d ? accent : border}`, borderRadius: 10, background: difficulty === d ? (darkMode ? "#1e3a5f" : "#eff6ff") : card, color: difficulty === d ? accent : muted, fontWeight: difficulty === d ? 700 : 400, fontSize: 14, cursor: "pointer" }}>
                  {d === "easy" ? "😊 Easy" : d === "medium" ? "🧠 Medium" : "🔥 Hard"}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 28 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: muted, display: "block", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Number of questions</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[5, 10, 15].map((n) => (
                <button key={n} onClick={() => setQuestionCount(n)} style={{ flex: 1, padding: "9px 0", border: `1px solid ${questionCount === n ? accent : border}`, borderRadius: 10, background: questionCount === n ? (darkMode ? "#1e3a5f" : "#eff6ff") : card, color: questionCount === n ? accent : muted, fontWeight: questionCount === n ? 700 : 400, fontSize: 14, cursor: "pointer" }}>
                  {n}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div style={{ background: darkMode ? "#1a0a0a" : "#fef2f2", border: "1px solid #dc2626", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#dc2626", marginBottom: 16 }}>
              {error}
            </div>
          )}

          <button onClick={startQuiz} style={{ width: "100%", padding: "14px 0", background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: "pointer" }}>
            Start Quiz →
          </button>
        </div>
      </div>
    );
  }

  if (stage === "error") {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: bg, gap: 16, padding: 20 }}>
        <div style={{ fontSize: 32 }}>⚠️</div>
        <p style={{ fontSize: 15, color: muted, textAlign: "center" }}>{error || "Couldn't generate quiz. Please try again."}</p>
        <button onClick={onExit} style={{ padding: "10px 24px", background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Back to chat</button>
      </div>
    );
  }

  if (stage === "loading") {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: bg, gap: 16 }}>
        <div style={{ fontSize: 32 }}>✨</div>
        <p style={{ fontSize: 15, color: muted }}>Generating your {subject} quiz...</p>
        <div style={{ display: "flex", gap: 6 }}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ width: 8, height: 8, borderRadius: "50%", background: accent, animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />
          ))}
        </div>
        <style>{`@keyframes bounce { 0%,80%,100%{transform:scale(0);opacity:0.3} 40%{transform:scale(1);opacity:1} }`}</style>
      </div>
    );
  }

  if (stage === "score") {
    const pctScore = Math.round((score / questions.length) * 100);
    const msg = pctScore >= 90 ? "Outstanding! You've mastered this material." : pctScore >= 70 ? "Great work! Review the ones you missed." : pctScore >= 50 ? "Good effort — keep practicing and you'll get there." : "More practice needed. Try easy mode to build your foundation.";
    return (
      <div style={{ flex: 1, overflowY: "auto", background: bg }}>
        <div style={{ padding: "20px 16px", maxWidth: 500, margin: "0 auto" }}>
          <button onClick={onExit} style={{ background: "transparent", border: "none", color: muted, cursor: "pointer", fontSize: 13, padding: 0, marginBottom: 24, display: "block" }}>← Back to chat</button>
          <div style={{ textAlign: "center", marginBottom: 32 }}>
            <div style={{ width: 100, height: 100, borderRadius: "50%", border: `4px solid ${pctScore >= 70 ? "#16a34a" : pctScore >= 50 ? "#f59e0b" : "#dc2626"}`, display: "inline-flex", flexDirection: "column", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
              <span style={{ fontSize: 26, fontWeight: 700, color: text }}>{score}/{questions.length}</span>
              <span style={{ fontSize: 12, color: muted }}>{pctScore}%</span>
            </div>
            <p style={{ fontSize: 16, fontWeight: 600, color: text, marginBottom: 6 }}>{msg}</p>
            <p style={{ fontSize: 13, color: muted }}>{subject} • {difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}</p>
          </div>
          {wrongAnswers.length > 0 && (
            <div style={{ marginBottom: 28 }}>
              <p style={{ fontSize: 13, fontWeight: 700, color: muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 12 }}>Review missed questions</p>
              {wrongAnswers.map((w, i) => (
                <div key={i} style={{ background: card, border: `1px solid ${border}`, borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
                  <p style={{ fontSize: 13, color: text, fontWeight: 500, marginBottom: 8 }}>{w.question}</p>
                  <p style={{ fontSize: 12, color: "#dc2626", marginBottom: 4 }}>Your answer: {w.options[w.chosen]}</p>
                  <p style={{ fontSize: 12, color: "#16a34a", marginBottom: 8 }}>Correct: {w.options[w.correct]}</p>
                  <p style={{ fontSize: 12, color: muted, lineHeight: 1.6 }}>{w.explanation}</p>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <button onClick={retryQuiz} style={{ padding: "12px 0", background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>Retry this quiz</button>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => newQuiz("easy")} style={{ flex: 1, padding: "10px 0", background: card, border: `1px solid ${border}`, borderRadius: 10, color: text, fontSize: 13, cursor: "pointer" }}>😊 Easier</button>
              <button onClick={() => newQuiz("hard")} style={{ flex: 1, padding: "10px 0", background: card, border: `1px solid ${border}`, borderRadius: 10, color: text, fontSize: 13, cursor: "pointer" }}>🔥 Harder</button>
              <button onClick={() => newQuiz(null)} style={{ flex: 1, padding: "10px 0", background: card, border: `1px solid ${border}`, borderRadius: 10, color: text, fontSize: 13, cursor: "pointer" }}>New topic</button>
            </div>
            <button onClick={onExit} style={{ padding: "10px 0", background: "transparent", border: `1px solid ${border}`, borderRadius: 10, color: muted, fontSize: 13, cursor: "pointer" }}>Back to chat</button>
          </div>
        </div>
      </div>
    );
  }

  // quiz stage
  return (
    <div style={{ flex: 1, overflowY: "auto", background: bg }}>
      <div style={{ padding: "20px 16px", maxWidth: 500, margin: "0 auto" }}>
        <div style={{ height: 4, background: border, borderRadius: 99, marginBottom: 20, overflow: "hidden" }}>
          <div style={{ height: "100%", width: pct + "%", background: accent, borderRadius: 99, transition: "width 0.4s" }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <span style={{ fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 99, background: darkMode ? "#1e3a5f" : "#eff6ff", color: accent }}>{q.category}</span>
          <span style={{ fontSize: 13, color: muted }}>{current + 1} / {questions.length}</span>
        </div>
        <p style={{ fontSize: 15, fontWeight: 500, color: text, lineHeight: 1.6, marginBottom: q.graph ? 12 : 20 }}>{q.question}</p>
        {q.graph && /[xy]/.test(q.graph) && q.graph.length < 80 && !q.graph.includes(" a ") && !q.graph.includes("imagine") && !q.graph.includes("consider") && (
          <QuizGraph expr={q.graph} darkMode={darkMode} />
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
          {q.options.map((opt, i) => {
            let borderColor = border;
            let bgColor = card;
            let letterBg = "transparent";
            let letterColor = muted;
            let letterBorder = border;
            if (answered) {
              if (i === q.correct) { borderColor = "#16a34a"; bgColor = darkMode ? "#0a2e1a" : "#f0fdf4"; letterBg = "#16a34a"; letterColor = "#fff"; letterBorder = "#16a34a"; }
              else if (i === selected && i !== q.correct) { borderColor = "#dc2626"; bgColor = darkMode ? "#2e0a0a" : "#fef2f2"; letterBg = "#dc2626"; letterColor = "#fff"; letterBorder = "#dc2626"; }
            } else if (selected === i) { borderColor = accent; bgColor = darkMode ? "#1e3a5f" : "#eff6ff"; letterBg = accent; letterColor = "#fff"; letterBorder = accent; }
            return (
              <div key={i} onClick={() => handleSelect(i)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", border: `1px solid ${borderColor}`, borderRadius: 12, background: bgColor, cursor: answered ? "default" : "pointer", transition: "all 0.15s" }}>
                <div style={{ width: 28, height: 28, borderRadius: "50%", border: `1px solid ${letterBorder}`, background: letterBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, flexShrink: 0, color: letterColor, transition: "all 0.15s" }}>
                  {["A", "B", "C", "D"][i]}
                </div>
                <span style={{ fontSize: 14, color: text, lineHeight: 1.5 }}>{opt}</span>
              </div>
            );
          })}
        </div>
        {answered && (
          <div style={{ padding: "12px 16px", background: darkMode ? "#0d1f0d" : "#f0fdf4", borderLeft: "3px solid #16a34a", borderRadius: "0 10px 10px 0", marginBottom: 16 }}>
            <p style={{ fontSize: 13, color: darkMode ? "#86efac" : "#15803d", lineHeight: 1.6, margin: 0 }}>
              {selected === q.correct ? "✓ Correct! " : "✗ Incorrect. "}{q.explanation}
            </p>
          </div>
        )}
        <button onClick={handleNext} disabled={!answered} style={{ width: "100%", padding: "13px 0", background: answered ? "linear-gradient(135deg, #3b82f6, #2563eb)" : border, color: answered ? "#fff" : muted, border: "none", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: answered ? "pointer" : "not-allowed", transition: "all 0.2s" }}>
          {current + 1 === questions.length ? "See results →" : "Next question →"}
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const [screen, setScreen] = useState("home");
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showLogin, setShowLogin] = useState(false);
  const [plan, setPlan] = useState("free");
  const [showPromo, setShowPromo] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [subject, setSubject] = useState("Math");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [usageToday, setUsageToday] = useState(0);
  const [upgradeReason, setUpgradeReason] = useState("");
  const [toastMsg, setToastMsg] = useState("");
  const [imagePreview, setImagePreview] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [darkMode, setDarkMode] = useState(true);
  const [quizMode, setQuizMode] = useState(false);
  const [humanizerInput, setHumanizerInput] = useState("");
  const [humanizerOutput, setHumanizerOutput] = useState("");
  const [humanizerLoading, setHumanizerLoading] = useState(false);
  const [humanizerTimer, setHumanizerTimer] = useState(0);
  const [detectorInput, setDetectorInput] = useState("");
  const [detectorResult, setDetectorResult] = useState(null);
  const [detectorLoading, setDetectorLoading] = useState(false);
  const [detectorTimer, setDetectorTimer] = useState(0);

  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const styles = getStyles(darkMode);

  useEffect(() => {
    const s = loadState();
    if (s.darkMode !== undefined) setDarkMode(s.darkMode);
    const count = loadUsage();
    setUsageToday(count);

    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setAuthLoading(false);
      if (firebaseUser) {
        if (firebaseUser.displayName) setDisplayName(firebaseUser.displayName.split(" ")[0]);
        try {
          const snap = await getDoc(doc(db, "users", firebaseUser.uid));
          if (snap.exists()) {
            const data = snap.data();
            const verifiedPlan = data.plan || "free";
            setPlan(verifiedPlan);
            const s2 = loadState();
            s2.plan = verifiedPlan;
            saveState(s2);
            if (data.darkMode !== undefined) setDarkMode(data.darkMode);
            if (data.name) setDisplayName(data.name.split(" ")[0]);
          } else {
            await setDoc(doc(db, "users", firebaseUser.uid), {
              name: firebaseUser.displayName || "",
              email: firebaseUser.email,
              plan: "free",
              createdAt: Date.now(),
            });
          }
        } catch {}
      } else {
        const s2 = loadState();
        if (s2.plan) setPlan(s2.plan);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("success") === "true") {
      const rawPlan = params.get("plan");
      const cleanPlan = rawPlan?.replace("_monthly", "");
      if (cleanPlan && PLANS[cleanPlan]) {
        const s = loadState();
        s.plan = cleanPlan;
        saveState(s);
        setPlan(cleanPlan);
        window.history.replaceState({}, "", "/");
        showToast("🎉 Welcome to " + PLANS[cleanPlan].name + "!");
        if (auth.currentUser) {
          updateDoc(doc(db, "users", auth.currentUser.uid), { plan: cleanPlan }).catch(() => {});
        }
      }
    }
  }, []);

  useEffect(() => {
    if (user) {
      getDoc(doc(db, "users", user.uid)).then(snap => {
        if (snap.exists()) {
          const verifiedPlan = snap.data().plan || "free";
          setPlan(verifiedPlan);
        }
      }).catch(() => {});
    }
  }, [screen, user]);

  useEffect(() => {
    async function loadHistory() {
      setMessages([]);
      setQuizMode(false);
      if (user) {
        const firestoreHistory = await loadSubjectHistoryFromFirestore(subject, user.uid);
        if (firestoreHistory && firestoreHistory.length > 0) {
          setMessages(firestoreHistory);
          return;
        }
      }
      const localHistory = loadSubjectHistory(subject);
      setMessages(localHistory);
    }
    loadHistory();
  }, [subject, user]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({behavior: "smooth"});
  }, [messages, loading]);

  function toggleDarkMode() {
    const newMode = !darkMode;
    setDarkMode(newMode);
    const s = loadState();
    s.darkMode = newMode;
    saveState(s);
  }

  function showToast(msg) {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 3000);
  }

  function goToSubject(s) {
    setSubject(s);
    setScreen("chat");
  }

  async function selectPlan(p) {
    if (p === "free") {
      const s = loadState();
      s.plan = p;
      saveState(s);
      setPlan(p);
      if (user) {
        try { await updateDoc(doc(db, "users", user.uid), { plan: p }); } catch {}
      }
      setScreen("chat");
      return;
    }
    if (!user) {
      setShowLogin(true);
      showToast("Please sign in to upgrade your plan.");
      return;
    }
    try {
      const response = await fetch("https://createcheckoutsession-mo6g2pop4q-uc.a.run.app", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({plan: p + "_monthly"}),
      });
      const data = await response.json();
      if (data.url) window.location.href = data.url;
    } catch {
      showToast("Payment error. Please try again.");
    }
  }

  async function handleLogout() {
    try {
      await signOut(auth);
      setPlan("free");
      setUsageToday(0);
      showToast("Signed out successfully.");
    } catch {}
  }

  async function redeemPromo() {
    if (!promoCode.trim()) return;
    if (!user) { showToast("Please sign in first to redeem a promo code."); setShowPromo(false); return; }
    setPromoLoading(true);
    try {
      const response = await fetch("https://createcheckoutsession-mo6g2pop4q-uc.a.run.app/redeemPromo", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({code: promoCode.trim().toUpperCase(), userId: user.uid}),
      });
      const data = await response.json();
      if (data.success) {
        setPlan(data.plan);
        const s = loadState();
        s.plan = data.plan;
        saveState(s);
        showToast("🎉 Promo applied! Welcome to " + PLANS[data.plan].name + "!");
        setShowPromo(false);
        setPromoCode("");
      } else {
        showToast("Invalid or expired promo code.");
      }
    } catch {
      showToast("Error redeeming code. Please try again.");
    }
    setPromoLoading(false);
  }

  function bumpUsage() {
    const newCount = usageToday + 1;
    setUsageToday(newCount);
    saveUsage(newCount);
    const s = loadState();
    s.usageDate = getTodayKey();
    s.usageToday = newCount;
    saveState(s);
  }

  function canAsk() {
    const limit = PLANS[plan].questionsPerDay;
    if (limit === Infinity) return true;
    return usageToday < limit;
  }

  async function handleImageSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    const preview = URL.createObjectURL(file);
    setImagePreview(preview);
    const b64 = await fileToBase64(file);
    setImageBase64(b64);
    showToast("📸 Image ready! Ask your question or just hit Send.");
  }

  function clearImage() {
    setImagePreview(null);
    setImageBase64(null);
  }

  async function runHumanizer() {
    if (!humanizerInput.trim()) return;
    setHumanizerLoading(true);
    setHumanizerOutput("");
    setHumanizerTimer(0);
    let secs = 0;
    const timer = setInterval(() => { secs++; setHumanizerTimer(secs); }, 1000);
    try {
      const result = await callAI(
        `You are an expert editor who rewrites AI-generated text to sound like a real human wrote it. Your goal is to completely fool AI detectors.

CRITICAL RULES — every single one must be followed:
- ZERO dashes of any kind. No em dash (—), no en dash (–), no hyphen used as a dash. Replace them with commas, periods, or restructure the sentence.
- ZERO semicolons. Replace with periods or commas.
- NO formal transition words: no "Furthermore", "Moreover", "Additionally", "In conclusion", "It is important to note", "It should be noted", "This demonstrates", "This highlights", "In summary".
- NO passive voice. Rewrite every passive sentence as active.
- Scramble sentence structure throughout. Move clauses around. Start sentences differently — with verbs, with time phrases, with subordinate clauses. If the original says "X is Y because Z", rewrite it as "Because Z, X ends up being Y" or "Z is what makes X Y".
- Use contractions naturally: it's, you're, they're, won't, can't, that's, here's.
- Mix short punchy sentences (under 8 words) with longer flowing ones. Vary rhythm deliberately.
- Sound like a sharp college student writing fast, not a textbook.
- Keep ALL the original meaning and facts — just rewrite the delivery completely.
- Output ONLY the rewritten text. No preamble, no explanation, nothing else.`,
        "Please rewrite this text to sound more human and natural:\n\n" + humanizerInput
      );
      setHumanizerOutput(result);
    } catch {
      setHumanizerOutput("Error — please try again.");
    }
    clearInterval(timer);
    setHumanizerTimer(0);
    setHumanizerLoading(false);
  }

  async function runDetector() {
    if (!detectorInput.trim()) return;
    setDetectorLoading(true);
    setDetectorResult(null);
    setDetectorTimer(0);
    let secs = 0;
    const timer = setInterval(() => { secs++; setDetectorTimer(secs); }, 1000);
    try {
      const detectorResponse = await fetch("https://us-central1-scholarx-6440c.cloudfunctions.net/aiProxy", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "HTTP-Referer": "https://scholarsensai.com",
        },
        body: JSON.stringify({
          model: "openai/gpt-4o-mini",
          max_tokens: 800,
          response_format: { type: "json_object" },
          messages: [
            { role: "system", content: `You are an AI content detector. Analyze text and return JSON only.

Score from 0-100 (probability text is AI-written). Start at 50, adjust based on signals.

HUMAN signals (subtract points): personal stories (-10), casual language like "honestly/yeah/kind of" (-8), emotional opinions (-8), varied sentence lengths (-8), specific memories (-10)
AI signals (add points): formal transitions like "Furthermore/Moreover/It is important to note" (+10), uniform sentence structure (+8), generic claims without examples (+8), passive voice (+6)

NEVER flag casual phrases as AI. NEVER count topic-word repetition as AI signal.
Score above 75% only if 4+ AI signals AND no human signals.

Return this exact JSON:
{"score":42,"verdict":"Mixed Content","confidence":"Medium","human_signals":["reason"],"ai_signals":["reason"],"score_breakdown":[{"factor":"name","impact":"-10","note":"why"}],"flagged_phrases":["exact phrase"],"reasons":["analysis"],"suggestions":["tip"]}` },
            { role: "user", content: "Analyze this text:\n\n" + detectorInput }
          ],
        }),
      });
      const detectorData = await detectorResponse.json();
      const result = detectorData.choices?.[0]?.message?.content || "";
      try {
        const clean = result.replace(/```json|```/g, "").trim();
        // Extract just the JSON object even if there's extra text
        const jsonMatch = clean.match(/\{[\s\S]*\}/);
        if (!jsonMatch) throw new Error("No JSON");
        const parsed = JSON.parse(jsonMatch[0]);
        // Ensure required fields exist
        if (!parsed.score) parsed.score = 50;
        if (!parsed.verdict) parsed.verdict = "Mixed Content";
        if (!parsed.reasons) parsed.reasons = [];
        if (!parsed.human_signals) parsed.human_signals = [];
        if (!parsed.ai_signals) parsed.ai_signals = [];
        if (!parsed.score_breakdown) parsed.score_breakdown = [];
        if (!parsed.flagged_phrases) parsed.flagged_phrases = [];
        if (!parsed.suggestions) parsed.suggestions = [];
        setDetectorResult(parsed);
      } catch {
        // Try to extract partial data
        try {
          const scoreMatch = result.match(/"score"\s*:\s*(\d+)/);
          const verdictMatch = result.match(/"verdict"\s*:\s*"([^"]+)"/);
          setDetectorResult({
            score: scoreMatch ? parseInt(scoreMatch[1]) : 50,
            verdict: verdictMatch ? verdictMatch[1] : "Mixed Content",
            confidence: "Low",
            human_signals: [],
            ai_signals: [],
            score_breakdown: [],
            flagged_phrases: [],
            reasons: ["Analysis complete — detailed breakdown unavailable."],
            suggestions: []
          });
        } catch {
          setDetectorResult({score: 50, verdict: "Mixed Content", confidence: "Low", human_signals: [], ai_signals: [], score_breakdown: [], flagged_phrases: [], reasons: ["Could not parse result — try again."], suggestions: []});
        }
      }
    } catch {
      setDetectorResult({score: 0, verdict: "Error", reasons: ["Please try again."]});
    }
    clearInterval(timer);
    setDetectorTimer(0);
    setDetectorLoading(false);
  }

  async function sendMessage() {
    const q = input.trim();
    if (!q && !imageBase64) return;

    if (isQuizRequest(q)) {
      setQuizMode(true);
      setInput("");
      return;
    }

    if (isGraphRequest(q)) {
      let expr = extractGraphExpr(q);
      // If no equation found in user message, search recent messages for one
      if (!expr) {
        const recent = [...messages].reverse().slice(0, 6);
        for (const m of recent) {
          if (m.content) {
            // Look for y= pattern in message content
            const yMatch = m.content.match(/y\s*=\s*([0-9x+\-*/^().sincotaqrlbpe ]+)/i);
            if (yMatch) {
              const candidate = "y=" + yMatch[1].trim().replace(/\s+/g, "");
              const test = (() => { try { const e = candidate.replace(/y=/i,"").replace(/\^/g,"**").replace(/(\d)(x)/g,"$1*x"); return Function("x",`return (${e})`)(1); } catch { return NaN; } })();
              if (isFinite(test)) { expr = candidate; break; }
            }
          }
        }
      }
      if (expr) {
        const userMsg2 = {role: "user", content: q, subject};
        const graphMsg = {role: "assistant", content: `Here's the graph of ${expr.replace("y=", "")}`, graph: expr, subject};
        const updated = [...messages, userMsg2, graphMsg];
        setMessages(updated);
        saveSubjectHistory(subject, updated);
        setInput("");
        return;
      }
    }

    if (!canAsk()) {
      setUpgradeReason(`You've used all ${PLANS[plan].questionsPerDay} questions for today on the ${PLANS[plan].name} plan. Upgrade for more!`);
      setScreen("upgrade");
      return;
    }

    const userMsg = {role: "user", content: q || "Please solve the question in this image.", subject, image: imagePreview};
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    const sentImage = imageBase64;
    clearImage();
    setLoading(true);

    try {
      const systemPrompt = subject === "Math" ?
`You are ScholarSensAi, a concise and smart math tutor.
NEVER use ## or # for headers.
NEVER use $ or $$ or LaTeX.
NEVER use \\cdot or any backslash commands.
Write math in plain text only.
RULES:
- Identify the type of math and adjust accordingly
- Maximum 350 words total
- 4 to 6 steps only unless problem is very hard
- NEVER ramble, repeat, or think out loud
- Write each step on its own line with a blank line after it
- Use * for multiply, ^ for power, / for divide
- Give the final answer clearly at the end
- Be direct, clean, and efficient
- If asked to graph something, ONLY say: "Here is the graph of y=[equation]" — nothing else. Do NOT describe the graph in words.`
: subject === "Test Prep" ?
`You are ScholarSensAi, an expert test prep tutor with deep knowledge of all major standardized tests.
NEVER use ## or # for headers.
NEVER use $ or $$ or LaTeX.
NEVER use \\cdot or any backslash commands.
TESTS YOU KNOW: SAT, ACT, PSAT, AP exams, TEAS, NCLEX, MCAT, GRE, GMAT, LSAT, ASVAB, Praxis, CompTIA, GED, SOL, STAAR, Regents, MCAS, CAASPP, PARCC, FCAT, IB, PERT, Accuplacer, real estate exams, and more.
RULES:
- Identify which test the student is asking about and tailor your response accordingly
- Never tell a student you don't know a test or can't help
- Answer the student's specific question directly and completely
- If asked to make a quiz, number each question like 1) 2) 3) with a blank line between each
- Maximum 500 words
- Be direct, specific, and practical`
: subject === "Coding" ?
`You are ScholarSensAi, an expert coding tutor.
NEVER use ## or # for headers.
LANGUAGES YOU KNOW: Python, JavaScript, TypeScript, Java, C, C++, C#, HTML, CSS, SQL, Swift, Kotlin, Ruby, PHP, Go, Rust, and more.
RULES:
- Explain what the code does in plain English first
- Show clean formatted code examples
- Point out bugs clearly and explain the fix
- Maximum 400 words
- Be direct and practical`
: subject === "Science" ?
`You are ScholarSensAi, an expert science tutor.
NEVER use ## or # for headers.
NEVER use $ or $$ or LaTeX.
NEVER use \\cdot or any backslash commands.
SUBJECTS: Biology, Chemistry, Physics, Earth Science, Anatomy and Physiology, Microbiology, Environmental Science.
RULES:
- Connect concepts to real world examples
- For chemistry and physics, show calculations in plain text step by step
- For biology, use clear analogies
- Maximum 400 words
- Be clear, accurate, and engaging`
: subject === "Languages" ?
`You are ScholarSensAi, a language tutor. Be extremely concise.
RULES:
- For any translation or "how do you say" question use this exact format:

[word/phrase in target language] ([romanization if needed]) — [meaning]

Example: "[example sentence in target language]" = "[English translation]"

- One bonus related phrase only if genuinely useful, same format
- Do NOT give pronunciation guides unless asked
- Do NOT explain culture or history unless asked
- Do NOT offer to teach more or ask follow up questions
- Do NOT use filler phrases
- For grammar questions: one rule, one example, done
- Maximum 80 words for translation questions
- Maximum 150 words for grammar questions`
: subject === "History" ?
`You are ScholarSensAi, a clear and engaging history tutor.
NEVER use ## or # for headers.
AREAS: US History, World History, European History, Ancient History, AP courses, Geography, Political Science.
RULES:
- Give brief context before diving into facts
- Connect causes to effects clearly
- Use specific dates, names, and events
- Maximum 400 words`
: subject === "College Essays" ?
`You are ScholarSensAi, an expert college essay coach.
NEVER use ## or # for headers.
RULES:
- First message always: ask the student to share a sample of their own writing so you can learn their voice
- All suggestions must sound like THEM, not generic AI
- Flag clichés and suggest authentic alternatives
- Ask about the prompt, school, and story before giving feedback
- Maximum 1000 words per response
- Be encouraging but honest`
: subject === "English" ?
`You are ScholarSensAi, a sharp writing coach.
NEVER use ## or # for headers.
RULES:
- If no writing shared yet, ask ONE question: what's the topic and assignment type?
- Give specific direct feedback
- Suggest exact rewrites, not vague advice
- Maximum 350 words
- Never ramble or over-explain`
:
`You are ScholarSensAi, a smart and concise tutor for ${subject}.
NEVER use ## or # for headers.
RULES:
- Maximum 350 words
- Be clear, direct, and helpful
- Use simple language students can understand`;

      let userContent;
      if (sentImage) {
        // Detect media type from base64 header
        const mediaType = sentImage.startsWith("/9j/") ? "image/jpeg" : 
                         sentImage.startsWith("iVBOR") ? "image/png" :
                         sentImage.startsWith("R0lGO") ? "image/gif" : "image/jpeg";
        userContent = [
          {type: "image_url", image_url: {url: `data:${mediaType};base64,${sentImage}`, detail: "high"}},
          {type: "text", text: (q ? q + "\n\n" : "") + "Look at this image carefully. Identify every problem, equation, question, or diagram. Solve each one completely with full step-by-step working. Be concise and direct."},
        ];
      } else {
        userContent = q;
      }

      const apiMessages = [
        ...messages.map((m) => ({role: m.role, content: m.content})),
        {role: "user", content: userContent},
      ];

      const response = await fetch("https://us-central1-scholarx-6440c.cloudfunctions.net/aiProxy", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "HTTP-Referer": "https://scholarx-6440c.web.app",
        },
        body: JSON.stringify({
          model: sentImage ? "anthropic/claude-3-haiku" : "meta-llama/llama-3.3-70b-instruct",
          max_tokens: 1500,
          messages: [{role: "system", content: systemPrompt}, ...apiMessages],
        }),
      });

      const data = await response.json();
      const reply = data.choices?.[0]?.message?.content || "";
      const apiError = data.error?.message || "";

      if (!reply || reply.trim().length < 10) {
        const errMsg = apiError ? `⚠️ ${apiError}` : "⚠️ I had trouble with that one. Please try rephrasing or upload a clearer image!";
        const updatedMessages = [...newMessages, {role: "assistant", content: errMsg}];
        setMessages(updatedMessages);
        saveSubjectHistory(subject, updatedMessages, user?.uid);
      } else {
        bumpUsage();
        const updatedMessages = [...newMessages, {role: "assistant", content: reply}];
        setMessages(updatedMessages);
        saveSubjectHistory(subject, updatedMessages, user?.uid);
      }
    } catch {
      const updatedMessages = [...newMessages, {role: "assistant", content: "⚠️ Connection error. Please check your internet and try again."}];
      setMessages(updatedMessages);
      saveSubjectHistory(subject, updatedMessages, user?.uid);
    }
    setLoading(false);
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function clearHistory() {
    const key = "scholarsensai_history_" + subject.replace(/\s+/g, "_");
    localStorage.removeItem(key);
    setMessages([]);
    if (user) {
      const firestoreKey = subject.replace(/\s+/g, "_");
      setDoc(doc(db, "users", user.uid, "history", firestoreKey), {
        messages: [], savedAt: Date.now()
      }).catch(() => {});
    }
    showToast("Chat history cleared!");
  }

  const currentPlan = PLANS[plan];
  const limitReached = currentPlan.questionsPerDay !== Infinity && usageToday >= currentPlan.questionsPerDay;
  const questionsLeft = currentPlan.questionsPerDay === Infinity ? "∞" : Math.max(0, currentPlan.questionsPerDay - usageToday);

  const Logo = () => (
    <>
      <span style={{marginRight: 4}}>🎓</span>
      <span style={{background: "linear-gradient(135deg, #3b82f6, #60a5fa)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"}}>ScholarSens</span>
      <span style={{background: "linear-gradient(135deg, #f59e0b, #fbbf24)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"}}>Ai</span>
    </>
  );

  if (screen === "humanizer") {
    return (
      <div style={styles.page}>
        <div style={{maxWidth: 600, width: "100%", paddingTop: 30}}>
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16}}>
            <button style={styles.backBtn} onClick={() => setScreen("home")}>← Home</button>
            <button style={styles.themeBtn} onClick={toggleDarkMode}>{darkMode ? "☀️" : "🌙"}</button>
          </div>
          <h2 style={{...styles.plansTitle, marginTop: 0}}>🧑 AI Humanizer</h2>
          <p style={styles.plansSub}>Paste AI-generated text and make it sound human</p>
          <p style={{fontSize: 11, color: darkMode ? "#475569" : "#94a3b8", marginBottom: 12, fontStyle: "italic"}}>Results may still be flagged by some detectors. Always review before submitting.</p>
          <textarea style={styles.toolTextarea} placeholder="Paste your AI-generated text here..." value={humanizerInput} onChange={(e) => setHumanizerInput(e.target.value)} rows={8} />
          <button style={{...styles.primaryBtn, opacity: humanizerLoading || !humanizerInput.trim() ? 0.5 : 1}} onClick={runHumanizer} disabled={humanizerLoading || !humanizerInput.trim()}>
            {humanizerLoading ? `✨ Humanizing... ${humanizerTimer}s` : "✨ Humanize Text"}
          </button>
          {humanizerOutput && (
            <div style={styles.resultBox}>
              <div style={styles.resultLabel}>✅ Humanized Version</div>
              <pre style={styles.resultText}>{humanizerOutput}</pre>
              <div style={{display: "flex", gap: 8, marginTop: 12}}>
                <button style={styles.copyBtn} onClick={() => { navigator.clipboard.writeText(humanizerOutput); showToast("Copied to clipboard!"); }}>Copy</button>
                <button style={styles.copyBtn} onClick={() => { setHumanizerInput(""); setHumanizerOutput(""); }}>Clear</button>
              </div>
            </div>
          )}
        </div>
        {toastMsg && <div style={styles.toast}>{toastMsg}</div>}
      </div>
    );
  }

  if (screen === "detector") {
    const scoreColor = detectorResult ? detectorResult.score > 70 ? "#ef4444" : detectorResult.score > 40 ? "#f59e0b" : "#22c55e" : "#3b82f6";
    return (
      <div style={styles.page}>
        <div style={{maxWidth: 600, width: "100%", paddingTop: 30}}>
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16}}>
            <button style={styles.backBtn} onClick={() => setScreen("home")}>← Home</button>
            <button style={styles.themeBtn} onClick={toggleDarkMode}>{darkMode ? "☀️" : "🌙"}</button>
          </div>
          <h2 style={{...styles.plansTitle, marginTop: 0}}>🔍 AI Detector</h2>
          <p style={styles.plansSub}>Find out if text was written by AI or a human</p>
          <p style={{fontSize: 11, color: darkMode ? "#475569" : "#94a3b8", marginBottom: 12, fontStyle: "italic"}}>Results are estimates only and may not be fully accurate.</p>
          {detectorResult && detectorResult.flagged_phrases && detectorResult.flagged_phrases.length > 0 ? (
            <div style={{...styles.toolTextarea, minHeight: 160, whiteSpace: "pre-wrap", wordBreak: "break-word", cursor: "text", lineHeight: 1.8}} onClick={() => setDetectorResult(null)}>
              {(() => {
                let text = detectorInput;
                let parts = [];
                let remaining = text;
                const phrases = [...detectorResult.flagged_phrases].sort((a,b) => b.length - a.length);
                // Build regex from all flagged phrases
                const escaped = phrases.map(p => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
                const regex = new RegExp(`(${escaped.join("|")})`, "gi");
                const segments = remaining.split(regex);
                return segments.map((seg, i) => {
                  const isHighlighted = phrases.some(p => p.toLowerCase() === seg.toLowerCase());
                  return isHighlighted ? (
                    <mark key={i} style={{background: "#f59e0b", color: "#000", borderRadius: 3, padding: "1px 2px"}}>{seg}</mark>
                  ) : (
                    <span key={i}>{seg}</span>
                  );
                });
              })()}
              <div style={{fontSize: 11, color: darkMode ? "#64748b" : "#94a3b8", marginTop: 8}}>Click to edit text</div>
            </div>
          ) : (
            <textarea style={styles.toolTextarea} placeholder="Paste text to analyze..." value={detectorInput} onChange={(e) => { setDetectorInput(e.target.value); setDetectorResult(null); }} rows={8} />
          )}
          <button style={{...styles.primaryBtn, opacity: detectorLoading || !detectorInput.trim() ? 0.5 : 1}} onClick={runDetector} disabled={detectorLoading || !detectorInput.trim()}>
            {detectorLoading ? `🔍 Analyzing... ${detectorTimer}s` : "🔍 Detect AI"}
          </button>
          {detectorResult && (
            <div style={styles.resultBox}>
              <div style={{textAlign: "center", marginBottom: 16}}>
                <div style={{fontSize: 56, fontWeight: 800, color: scoreColor}}>{detectorResult.score}%</div>
                <div style={{fontSize: 18, fontWeight: 700, color: scoreColor}}>
                  {detectorResult.score <= 25 ? "Likely Human" : detectorResult.score <= 50 ? "Mixed Content" : detectorResult.score <= 75 ? "Possibly AI Assisted" : "Likely AI Generated"}
                </div>
                <div style={{fontSize: 13, color: "#64748b", marginTop: 4}}>AI probability score</div>
                <div style={{width: "100%", background: darkMode ? "#0f172a" : "#e2e8f0", borderRadius: 8, height: 8, marginTop: 16}}>
                  <div style={{width: detectorResult.score + "%", height: "100%", borderRadius: 8, background: "linear-gradient(90deg, #22c55e, #f59e0b, #ef4444)"}} />
                </div>
                <div style={{display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b", marginTop: 4}}>
                  <span>Human</span><span>AI</span>
                </div>
              </div>
              {detectorResult.confidence && (
                <div style={{fontSize: 12, color: darkMode ? "#64748b" : "#94a3b8", textAlign: "center", marginBottom: 16}}>
                  Confidence: <strong style={{color: detectorResult.confidence === "High" ? "#ef4444" : detectorResult.confidence === "Medium" ? "#f59e0b" : "#22c55e"}}>{detectorResult.confidence}</strong>
                </div>
              )}
              {detectorResult.human_signals && detectorResult.human_signals.length > 0 && (
                <div style={{marginBottom: 14, background: darkMode ? "#0a2010" : "#f0fdf4", borderRadius: 10, padding: "12px 14px"}}>
                  <div style={{fontSize: 12, fontWeight: 700, color: "#22c55e", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em"}}>✓ Human Signals</div>
                  {detectorResult.human_signals.map((s, i) => (
                    <div key={i} style={{fontSize: 13, color: darkMode ? "#86efac" : "#166534", padding: "3px 0"}}>• {s}</div>
                  ))}
                </div>
              )}
              {detectorResult.ai_signals && detectorResult.ai_signals.length > 0 && (
                <div style={{marginBottom: 14, background: darkMode ? "#200a0a" : "#fef2f2", borderRadius: 10, padding: "12px 14px"}}>
                  <div style={{fontSize: 12, fontWeight: 700, color: "#ef4444", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em"}}>⚠ AI Signals</div>
                  {detectorResult.ai_signals.map((s, i) => (
                    <div key={i} style={{fontSize: 13, color: darkMode ? "#fca5a5" : "#991b1b", padding: "3px 0"}}>• {s}</div>
                  ))}
                </div>
              )}
              {detectorResult.score_breakdown && detectorResult.score_breakdown.length > 0 && (
                <div style={{marginBottom: 14}}>
                  <div style={{fontSize: 12, fontWeight: 700, color: darkMode ? "#94a3b8" : "#64748b", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em"}}>Score Breakdown</div>
                  {detectorResult.score_breakdown.map((b, i) => (
                    <div key={i} style={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${darkMode ? "#1e293b" : "#f1f5f9"}`}}>
                      <span style={{fontSize: 13, color: darkMode ? "#cbd5e1" : "#475569"}}>{b.factor}</span>
                      <span style={{fontSize: 13, fontWeight: 700, color: b.impact?.startsWith("+") ? "#ef4444" : "#22c55e"}}>{b.impact}</span>
                    </div>
                  ))}
                </div>
              )}
              {detectorResult.reasons && detectorResult.reasons.length > 0 && (
                <div style={{marginBottom: 14}}>
                  <div style={{fontSize: 12, fontWeight: 700, color: darkMode ? "#94a3b8" : "#64748b", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em"}}>Analysis</div>
                  {detectorResult.reasons.map((r, i) => (
                    <div key={i} style={{fontSize: 13, color: darkMode ? "#94a3b8" : "#475569", padding: "4px 0", lineHeight: 1.6}}>• {r}</div>
                  ))}
                </div>
              )}
              {detectorResult.suggestions && detectorResult.suggestions.length > 0 && (
                <div style={{marginBottom: 14, background: darkMode ? "#0f1f2e" : "#eff6ff", borderRadius: 10, padding: "12px 14px"}}>
                  <div style={{fontSize: 12, fontWeight: 700, color: "#3b82f6", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em"}}>💡 How to Sound More Human</div>
                  {detectorResult.suggestions.map((s, i) => (
                    <div key={i} style={{fontSize: 13, color: darkMode ? "#93c5fd" : "#1d4ed8", padding: "4px 0", lineHeight: 1.6}}>• {s}</div>
                  ))}
                </div>
              )}
              {detectorResult.flagged_phrases && detectorResult.flagged_phrases.length > 0 && (
                <div style={{marginTop: 16}}>
                  <div style={{fontSize: 12, fontWeight: 700, color: "#f59e0b", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em"}}>⚑ Flagged as AI-sounding:</div>
                  {detectorResult.flagged_phrases.map((phrase, i) => (
                    <div key={i} style={{fontSize: 13, background: darkMode ? "#2d1f00" : "#fef3c7", color: darkMode ? "#fbbf24" : "#92400e", border: `1px solid ${darkMode ? "#854d0e" : "#fcd34d"}`, borderRadius: 6, padding: "5px 10px", marginBottom: 6, fontStyle: "italic"}}>
                      "{phrase}"
                    </div>
                  ))}
                  <p style={{fontSize: 11, color: darkMode ? "#64748b" : "#94a3b8", marginTop: 8}}>These phrases triggered AI detection. Rewrite them to sound more natural.</p>
                </div>
              )}
              <button style={{...styles.copyBtn, marginTop: 16, width: "100%"}} onClick={() => { setDetectorInput(""); setDetectorResult(null); }}>Clear</button>
            </div>
          )}
        </div>
        {toastMsg && <div style={styles.toast}>{toastMsg}</div>}
      </div>
    );
  }

  if (screen === "grapher") {
    return (
      <div style={{minHeight: "100vh", background: darkMode ? "#080d1a" : "#f8fafc", color: darkMode ? "#f1f5f9" : "#0f172a", fontFamily: "'Segoe UI', system-ui, sans-serif", display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 12px"}}>
        <div style={{maxWidth: 700, width: "100%"}}>
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16}}>
            <button style={{background: "transparent", border: "none", color: darkMode ? "#94a3b8" : "#64748b", fontSize: 14, cursor: "pointer", padding: 4}} onClick={() => setScreen("home")}>← Home</button>
            <span style={{fontWeight: 700, fontSize: 16}}>📈 Graphing Calculator</span>
            <button style={{background: "transparent", border: `1px solid ${darkMode ? "#1e2d4a" : "#e2e8f0"}`, borderRadius: 20, padding: "4px 10px", fontSize: 16, cursor: "pointer"}} onClick={toggleDarkMode}>{darkMode ? "☀️" : "🌙"}</button>
          </div>
          <div style={{borderRadius: 16, overflow: "hidden", border: `1px solid ${darkMode ? "#1e2d4a" : "#e2e8f0"}`, marginBottom: 16}}>
            <iframe
              src={`https://www.desmos.com/calculator?lang=en${darkMode ? "&darkMode=true" : ""}`}
              style={{width: "100%", height: 520, border: "none", display: "block"}}
              title="Desmos Graphing Calculator"
              allowFullScreen
            />
          </div>
          <div style={{background: darkMode ? "#0f1729" : "#ffffff", border: `1px solid ${darkMode ? "#1e2d4a" : "#e2e8f0"}`, borderRadius: 12, padding: "12px 16px"}}>
            <p style={{fontSize: 12, fontWeight: 700, color: "#3b82f6", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em"}}>Quick Tips</p>
            <p style={{fontSize: 12, color: darkMode ? "#94a3b8" : "#64748b", lineHeight: 1.8, margin: 0}}>
              Type any function like <strong>y = x^2</strong> or <strong>y = sin(x)</strong><br/>
              Use sliders by typing <strong>a</strong> or <strong>b</strong> as variables<br/>
              Plot multiple functions to compare them<br/>
              Pinch to zoom, drag to pan
            </p>
          </div>
        </div>
        {toastMsg && <div style={{position:"fixed",bottom:30,left:"50%",transform:"translateX(-50%)",background:darkMode?"#0f1729":"#fff",border:`1px solid ${darkMode?"#1e2d4a":"#e2e8f0"}`,color:darkMode?"#f1f5f9":"#0f172a",padding:"10px 20px",borderRadius:12,fontSize:14,fontWeight:600,zIndex:999}}>{toastMsg}</div>}
      </div>
    );
  }

  if (showLogin) {
    return <LoginScreen darkMode={darkMode} onSuccess={() => setShowLogin(false)} />;
  }

  if (authLoading) {
    return (
      <div style={{minHeight: "100vh", background: darkMode ? "#080d1a" : "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center"}}>
        <div style={{fontSize: 32}}>🎓</div>
      </div>
    );
  }

  if (screen === "settings") {
    return <SettingsScreen
      darkMode={darkMode}
      toggleDarkMode={toggleDarkMode}
      user={user}
      plan={plan}
      displayName={displayName}
      setDisplayName={setDisplayName}
      onBack={() => setScreen("home")}
      onLogout={handleLogout}
      onManagePlan={() => setScreen("plans")}
      showToast={showToast}
      db={db}
      toastMsg={toastMsg}
    />;
  }

  if (screen === "home") {
    return (
      <div style={styles.page}>
        <div style={styles.homeWrap}>
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8}}>
            {user ? (
              <button onClick={() => setScreen("settings")} style={{fontSize: 13, fontWeight: 600, color: darkMode ? "#94a3b8" : "#64748b", background: "transparent", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 4}}>
                ⚙️ {displayName || user.email?.split("@")[0]}
              </button>
            ) : (
              <button onClick={() => setShowLogin(true)} style={{fontSize: 13, fontWeight: 600, color: "#3b82f6", background: "transparent", border: "none", cursor: "pointer", padding: 0}}>Sign in</button>
            )}
            <button style={styles.themeBtn} onClick={toggleDarkMode}>{darkMode ? "☀️" : "🌙"}</button>
          </div>
          <div style={{fontSize: 34, fontWeight: 800, letterSpacing: "-1px", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "center"}}>
            <Logo />
          </div>
          <h1 style={styles.homeTitle}>{displayName ? `Hello, ${displayName}.` : "Your AI Homework Sensei"}</h1>
          <p style={styles.homeSubtitle}>{displayName ? "Ready to study? Pick a subject or ask anything!" : "Get instant help on any subject. Snap a photo or type your question!"}</p>
          <div style={styles.subjectGrid}>
            {["Math", "Science", "History", "English", "Coding", "Test Prep"].map((s) => (
              <button key={s} style={styles.subjectPillBtn} onClick={() => goToSubject(s)}>{s}</button>
            ))}
          </div>
          <button style={styles.primaryBtn} onClick={() => setScreen("chat")}>Start Studying →</button>
          <div style={styles.toolsRow}>
            <button style={styles.toolBtn} onClick={() => setScreen("humanizer")}>
              <span style={{fontSize: 24}}>🧑</span>
              <span style={{fontWeight: 700, fontSize: 14}}>AI Humanizer</span>
              <span style={{fontSize: 12, color: "#64748b"}}>Make AI text sound human</span>
            </button>
            <button style={styles.toolBtn} onClick={() => setScreen("detector")}>
              <span style={{fontSize: 24}}>🔍</span>
              <span style={{fontWeight: 700, fontSize: 14}}>AI Detector</span>
              <span style={{fontSize: 12, color: "#64748b"}}>Detect AI-written text</span>
            </button>
          </div>
          <div style={styles.toolsRow}>
            <button style={styles.toolBtn} onClick={() => setScreen("grapher")}>
              <span style={{fontSize: 24}}>📈</span>
              <span style={{fontWeight: 700, fontSize: 14}}>Graphing Calculator</span>
              <span style={{fontSize: 12, color: "#64748b"}}>Plot and analyze functions</span>
            </button>
          </div>
          <button style={styles.ghostBtn} onClick={() => setScreen("plans")}>View Plans</button>
          {showPromo ? (
            <div style={{display: "flex", gap: 8, marginBottom: 12}}>
              <input
                value={promoCode}
                onChange={e => setPromoCode(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === "Enter" && redeemPromo()}
                placeholder="Enter promo code"
                style={{flex: 1, background: darkMode ? "#0f1729" : "#fff", border: `1px solid ${darkMode ? "#1e2d4a" : "#e2e8f0"}`, borderRadius: 10, color: darkMode ? "#f1f5f9" : "#0f172a", fontSize: 14, padding: "10px 14px", outline: "none", fontFamily: "inherit"}}
              />
              <button onClick={redeemPromo} disabled={promoLoading} style={{padding: "10px 16px", background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer"}}>
                {promoLoading ? "..." : "Apply"}
              </button>
              <button onClick={() => setShowPromo(false)} style={{padding: "10px 12px", background: "transparent", border: `1px solid ${darkMode ? "#1e2d4a" : "#e2e8f0"}`, borderRadius: 10, color: darkMode ? "#94a3b8" : "#64748b", fontSize: 14, cursor: "pointer"}}>✕</button>
            </div>
          ) : (
            <button onClick={() => setShowPromo(true)} style={{background: "transparent", border: "none", color: darkMode ? "#64748b" : "#94a3b8", fontSize: 13, cursor: "pointer", marginBottom: 12, textDecoration: "underline"}}>Have a promo code?</button>
          )}
          <div style={styles.planBadge}>
            Currently on: <span style={{color: currentPlan.color, fontWeight: 700}}>{currentPlan.badge} {currentPlan.name}</span>
          </div>
        </div>
        {toastMsg && <div style={styles.toast}>{toastMsg}</div>}
      </div>
    );
  }

  if (screen === "plans") {
    return (
      <div style={styles.page}>
        <div style={styles.plansWrap}>
          <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16}}>
            <button style={styles.backBtn} onClick={() => setScreen("home")}>← Back</button>
            <button style={styles.themeBtn} onClick={toggleDarkMode}>{darkMode ? "☀️" : "🌙"}</button>
          </div>
          <h2 style={styles.plansTitle}>Choose Your Plan</h2>
          <p style={styles.plansSub}>Unlock your full academic potential</p>
          <div style={styles.plansGrid}>
            {Object.entries(PLANS).map(([key, p]) => (
              <div key={key} style={{...styles.planCard, borderColor: p.color, boxShadow: plan === key ? `0 0 0 3px ${p.color}44` : "none"}}>
                <div style={{fontSize: 32}}>{p.badge}</div>
                <div style={{...styles.planName, color: p.color}}>{p.name}</div>
                <div style={styles.planPrice}>{p.price}<span style={styles.planPeriod}>{p.period}</span></div>
                <ul style={styles.featureList}>
                  {p.features.map((f) => <li key={f} style={styles.featureItem}>✓ {f}</li>)}
                </ul>
                <button style={{...styles.planBtn, background: plan === key ? "#334155" : p.color, cursor: plan === key ? "default" : "pointer"}} onClick={() => plan !== key && selectPlan(key)}>
                  {plan === key ? "Current Plan" : `Get ${p.name}`}
                </button>
              </div>
            ))}
          </div>
        </div>
        {toastMsg && <div style={styles.toast}>{toastMsg}</div>}
      </div>
    );
  }

  if (screen === "upgrade") {
    return (
      <div style={styles.page}>
        <div style={styles.upgradeWrap}>
          <div style={{fontSize: 56}}>🚀</div>
          <h2 style={styles.upgradeTitle}>Unlock More with ScholarSensAi</h2>
          <p style={styles.upgradeReason}>{upgradeReason}</p>
          <div style={styles.upgradePlans}>
            {["scholar", "genius"].map((key) => {
              const p = PLANS[key];
              return (
                <div key={key} style={{...styles.upgradeCard, borderColor: p.color}}>
                  <div style={{fontSize: 28}}>{p.badge}</div>
                  <div style={{...styles.planName, color: p.color}}>{p.name}</div>
                  <div style={styles.planPrice}>{p.price}<span style={styles.planPeriod}>{p.period}</span></div>
                  <button style={{...styles.planBtn, background: p.color}} onClick={() => selectPlan(key)}>Upgrade to {p.name}</button>
                </div>
              );
            })}
          </div>
          <button style={styles.ghostBtn} onClick={() => setScreen("chat")}>Maybe later</button>
        </div>
      </div>
    );
  }

  // ── Chat screen ──
  return (
    <div style={styles.chatPage}>
      <div style={styles.chatHeader}>
        <button style={styles.backBtn} onClick={() => setScreen("home")}>← Home</button>
        <div style={styles.headerCenter}>
          <span style={{fontWeight: 800, fontSize: 16, display: "inline-flex", alignItems: "center"}}><Logo /></span>
        </div>
        <div style={{display: "flex", gap: 8, alignItems: "center"}}>
          <button style={styles.themeBtn} onClick={toggleDarkMode}>{darkMode ? "☀️" : "🌙"}</button>
          <button style={{...styles.planChip, background: currentPlan.color + "22", color: currentPlan.color, borderColor: currentPlan.color}} onClick={() => setScreen("plans")}>
            {currentPlan.badge} {currentPlan.name}
          </button>
        </div>
      </div>

      {quizMode ? (
        <QuizMode subject={subject} darkMode={darkMode} onExit={() => setQuizMode(false)} />
      ) : (
        <>
          <div style={styles.subjectBar}>
            {SUBJECTS.map((s) => (
              <button key={s} style={{...styles.subjectTab, background: subject === s ? "#3b82f6" : (darkMode ? "#0f1729" : "#f1f5f9"), color: subject === s ? "#fff" : (darkMode ? "#cbd5e1" : "#475569"), borderColor: subject === s ? "#3b82f6" : "transparent"}} onClick={() => setSubject(s)}>
                {s}
              </button>
            ))}
          </div>

          <div style={styles.usageBar}>
            <span style={{color: "#64748b", fontSize: 13}}>
              Questions left today: <strong style={{color: limitReached ? "#ef4444" : "#3b82f6"}}>{questionsLeft}</strong>
            </span>
            <div style={{display: "flex", gap: 8, alignItems: "center"}}>
              {messages.length > 0 && <button style={styles.clearBtn} onClick={clearHistory}>Clear</button>}
              {limitReached && (
                <button style={styles.upgradeInlineBtn} onClick={() => { setUpgradeReason("You've hit your daily limit. Upgrade for more questions!"); setScreen("upgrade"); }}>Upgrade ↑</button>
              )}
            </div>
          </div>

          <div style={styles.messagesArea}>
            {messages.length === 0 && (
              <div style={styles.emptyState}>
                <div style={{fontSize: 48, marginBottom: 12}}>🎓</div>
                <div style={styles.emptyTitle}>Hi! I'm your ScholarSensAi.</div>
                <div style={styles.emptySubtitle}>Type a question or 📸 snap a photo of your homework!</div>
                <div style={styles.examplesRow}>
                  {getExamples(subject).map((ex) => (
                    <button key={ex} style={styles.exampleChip} onClick={() => setInput(ex)}>{ex}</button>
                  ))}
                </div>
                {subject !== "College Essays" && (
                  <button onClick={() => setQuizMode(true)} style={{marginTop: 16, padding: "10px 24px", background: "linear-gradient(135deg, #3b82f6, #2563eb)", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer"}}>
                    🎯 Quiz Me
                  </button>
                )}
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} style={{...styles.msgWrap, justifyContent: m.role === "user" ? "flex-end" : "flex-start"}}>
                {m.role === "assistant" && <div style={styles.avatarAI}>🎓</div>}
                <div style={{...styles.bubble, background: m.role === "user" ? "linear-gradient(135deg, #3b82f6, #2563eb)" : (darkMode ? "#1e293b" : "#f1f5f9"), color: m.role === "user" ? "#fff" : (darkMode ? "#f1f5f9" : "#0f172a"), borderRadius: m.role === "user" ? "18px 18px 4px 18px" : "18px 18px 18px 4px", border: m.role === "assistant" && !darkMode ? "1px solid #e2e8f0" : "none"}}>
                  {m.role === "user" && <div style={styles.msgSubject}>{m.subject}</div>}
                  {m.image && <img src={m.image} alt="uploaded" style={{width: "100%", borderRadius: 8, marginBottom: 6, maxHeight: 200, objectFit: "contain"}} />}
                  {m.graph && <ChatGraph expr={m.graph} darkMode={darkMode} />}
                  {m.content && (
                    <pre style={styles.msgText}>
                      {m.content.split('\n').map((line, li) => (
                        <span key={li} style={{display: 'block', background: line.startsWith('✅ Final Answer') ? '#854d0e' : 'transparent', color: line.startsWith('✅ Final Answer') ? '#fef08a' : 'inherit', borderRadius: line.startsWith('✅ Final Answer') ? 6 : 0, padding: line.startsWith('✅ Final Answer') ? '4px 8px' : 0, fontWeight: line.startsWith('✅ Final Answer') ? 700 : 'inherit', marginBottom: line === '' ? 12 : 6}} dangerouslySetInnerHTML={{__html: formatMath(line)}} />
                      ))}
                    </pre>
                  )}
                </div>
                {m.role === "assistant" && (
                  <button onClick={() => { navigator.clipboard.writeText(m.content); showToast("Copied!"); }} style={{background: "transparent", border: "none", cursor: "pointer", color: "#64748b", padding: "4px 6px", alignSelf: "flex-end", marginBottom: 4, flexShrink: 0, opacity: 0.6}} title="Copy response"
                    onMouseEnter={e => e.currentTarget.style.opacity = "1"}
                    onMouseLeave={e => e.currentTarget.style.opacity = "0.6"}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  </button>
                )}
                {m.role === "user" && <div style={styles.avatarUser}>👤</div>}
              </div>
            ))}

            {loading && (
              <div style={{...styles.msgWrap, justifyContent: "flex-start"}}>
                <div style={styles.avatarAI}>🎓</div>
                <div style={{...styles.bubble, background: darkMode ? "#1e293b" : "#f1f5f9", border: !darkMode ? "1px solid #e2e8f0" : "none"}}>
                  <div style={{fontSize: 13, color: "#3b82f6", marginBottom: 8, fontWeight: 600}}>
                    {imagePreview ? "📸 Reading your image..." : "✍️ Thinking..."}
                  </div>
                  <div style={styles.typing}><span /><span /><span /></div>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {imagePreview && (
            <div style={styles.imagePreviewBar}>
              <img src={imagePreview} alt="preview" style={{height: 60, borderRadius: 8, objectFit: "contain"}} />
              <span style={{color: "#94a3b8", fontSize: 13, flex: 1}}>Image ready to send</span>
              <button style={styles.clearBtn} onClick={clearImage}>✕</button>
            </div>
          )}

          <div style={styles.inputArea}>
            <input type="file" accept="image/*" ref={fileInputRef} style={{display: "none"}} onChange={handleImageSelect} />
            <input type="file" accept="image/*" capture="environment" ref={cameraInputRef} style={{display: "none"}} onChange={handleImageSelect} />
            <button style={{background: "#3b82f6", border: "none", borderRadius: 10, padding: "9px 11px", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center"}} onClick={() => fileInputRef.current.click()} title="Attach image">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            </button>
            <button style={{background: "#3b82f6", border: "none", borderRadius: 10, padding: "9px 11px", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center"}} onClick={() => cameraInputRef.current.click()} title="Take photo">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
            </button>
            <textarea style={styles.textarea} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKey} placeholder={`Ask about ${subject} or snap a photo...`} rows={2} disabled={limitReached} />
            <button style={{...styles.sendBtn, opacity: (!input.trim() && !imageBase64) || limitReached ? 0.5 : 1}} onClick={sendMessage} disabled={(!input.trim() && !imageBase64) || limitReached}>
              {loading ? "→" : "Send"}
            </button>
          </div>
        </>
      )}

      {toastMsg && <div style={styles.toast}>{toastMsg}</div>}
    </div>
  );
}